import { useState, useEffect, forwardRef, useRef, useCallback, useMemo } from "react";
import styles from "../RightSidebar/rightsidebar.module.css";
import BorderedButton from "../Buttons/BorderedButton";
import PrimaryButton from "../Buttons/PrimaryButton";
import { toast } from "react-toastify";
import { useGlobalContext } from "../../Context/GlobalContext";
import DateTimeOutletSelector from "../RightSidebar/DateTimeOutletSelector";
import CustomerSelector from "./CustomerSelector";
import ServiceSelector from "./ServiceSelector/ServiceSelector";
import SourceSelector from "./SourceSelector";
import PriceSummary from "./PriceSummary";
import Notes from "./Notes";
import { isBelow12Tag } from "../DropDown/TaggingDropdown/TaggingDropdown";
import { buildTimeSelection, convertTo12Hour, convertToMinutes, extractHHMM, formatDateToDDMMYYYY, getTimeSelectionDayOffset, scrollToBooking, scrollToBookingCard, calculateEndTime, formatDateToYMD } from "../../Utils/utils";
import { buildProjectedOverridePayload } from "../../Utils/calendarProjection.js";
import {
	scheduleServiceMove,
	resolveServiceScheduleKey,
	ensureServiceLinkedFlags,
	markMismatchedEditChainLinksBroken,
} from "../../Utils/serviceScheduleChain";
import { editBookingData, getServicesList, getGuestCheckinList } from "../../APICalling/APICalling";
import UpdateRepeatBooking from "../Popups/UpdateRepeatBooking/UpdateRepeatBooking";
import RepeatBookingIcon from "../Icons/RepeatBookingIcon";
import ReloadIcon from "../Icons/ReloadIcon";
import { getBookingPreventChanges } from "../../Utils/BookingPreventChanges.js";
import { COMPLETE_BOOKING_WARNING_MESSAGE, PAYMENT_IN_PROGRESS_MESSAGE } from "../../Utils/Constants/errorMessages.js";

const EditSidebarWrapper = forwardRef(
(
	{
		interval,
		onClose,
		onUpdate,
		setShowBookingSummary,
		prefill,
		isOutletExtended,
	},
	ref
) => {
	const {
		editableBooking,
		setEditableBooking,
		setApiError,
		setItemData,
		itemData,
		homeStaffList,
		selectedServices,
		setSelectedServices,
		paxes,
		setPaxes,
		setBookingType,
		editedItemData,
		setEditedItemData,
		customerData,
		setCustomerData,
		setBookingData,
		bookingChanged,
		setBookingChanged,
		deletedBookingItemIds,
		selectedOutlet,
		selectedOutletData,
		selectedDate,
		selectedTime,
		setCreatedBooking,
		setSelectedTime,
		isMembershipApplied,
		setSelectedBookingId, 
		setIsBookingInfoUpdated,
		setNewAppointmentSlots,
		setShowConflictModal,
		loggedUser,
		setIsNewBookingSlotActive,
		selectedRooms,
		setSelectedRooms,
		setSelectedBookingUpdateScope,
		setSelectedBookingDeleteScope,
		guestCheckinList,
		setGuestCheckinList,
		selectedGuestCheckin,
		setSelectedGuestCheckin,
		completeBookingWarning,
		setCompleteBookingWarning,
		showPaymentInProgressNotice,
		setShowPaymentInProgressNotice,
		isGreyBox,
		isRoomFilterActive,
		selectedTherapistFromRow,
		calendarDateSyncSignal,
		setBookingsList,
		setIsEditBookingActive,
		setNewBookingActive,
		setDateSyncState,
		setEditPrefill,
	} = useGlobalContext();

	const { preventChanges, isBookingPaymentInProgress, isBookingCompleted, isBookingStatusReadOnly } = getBookingPreventChanges(editableBooking);
	const isPremiumOutlet = selectedOutletData?.outlet_type_id === 1; // 1 = Premium, 2 = Standard

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [editBooking, setEditBooking] = useState({
		date: null,
		time: null,
		outlet: editableBooking?.outlet_id || selectedOutlet,
	});
	const [customerError, setCustomerError] = useState("");
	const [serviceError, setServiceError] = useState("");
	const [showUpdateRepeatBooking, setShowUpdateRepeatBooking] = useState(false);
	const [repeatBookingOptionSelected, setRepeatBookingOptionSelected] = useState(false);
	const [apiErrorForCreateBooking, setApiErrorForCreateBooking] = useState("");
	const [saveValidationError, setSaveValidationError] = useState("");
	const [roomConflictErrors, setRoomConflictErrors] = useState([]);
	const [buttonDisabled, setButtonDisabled] = useState(false);
	const [overlappingErrors] = useState([]);
	
	const [overlappingErrorContainerHeight, setOverlappingErrorContainerHeight] = useState(0);
	const [guestCheckinPagination, setGuestCheckinPagination] = useState(null);
	const [guestCheckinLoadingMore, setGuestCheckinLoadingMore] = useState(false);
	const hydratedBookingItemsRef = useRef(null);
	const guestCheckinListRequestIdRef = useRef(0);
	const guestCheckinSearchDebounceRef = useRef(null);
	const lastGuestCheckinSearchRef = useRef("");
	const guestCheckinAppendInFlightRef = useRef(false);
	const overlappingErrorContainerRef = useRef(null);
	const resolveSavedBooleanFlag = useCallback((value, fallback = false) => {
		if (value === 1 || value === true || value === "1") return true;
		if (value === 0 || value === false || value === "0") return false;
		return Boolean(fallback);
	}, []);

	const normalizeBookingItemId = useCallback((value) => {
		if (value == null || value === "") return null;
		const normalized = Number(value);
		return Number.isNaN(normalized) ? null : normalized;
	}, []);

	const lockedBookingItemPricingById = useMemo(() => {
		if (!preventChanges || !editableBooking?.booking_item) {
			return new Map();
		}
		const map = new Map();
		Object.values(editableBooking.booking_item || {}).forEach((services) => {
			if (!Array.isArray(services)) return;
			services.forEach((svc) => {
				const bookingItemId = normalizeBookingItemId(svc?.id ?? svc?.booking_item_id);
				if (bookingItemId == null) return;
				map.set(bookingItemId, {
					isMemberRateApplied: svc?.isMemberRateApplied,
					isMembershipApplied: svc?.isMembershipApplied,
					price: svc?.price,
				});
			});
		});
		return map;
	}, [preventChanges, editableBooking?.booking_item, normalizeBookingItemId]);

	const getLockedBookingItemPricing = useCallback((serviceLike) => {
		if (!preventChanges) return null;
		const bookingItemId = normalizeBookingItemId(serviceLike?.booking_item_id ?? serviceLike?.id);
		if (bookingItemId == null) return null;
		return lockedBookingItemPricingById.get(bookingItemId) || null;
	}, [preventChanges, normalizeBookingItemId, lockedBookingItemPricingById]);

	const repeatBooking = editableBooking?.recurring_group_id !== null && editableBooking?.recurring_group_id !== undefined && editableBooking?.recurring_group_id !== '';

    const isOutletPanel = editableBooking?.panel?.toLowerCase() === "outlet";

	// Reset repeatBookingOptionSelected when editableBooking changes (new booking being edited)
	useEffect(() => {
		setRepeatBookingOptionSelected(false);
		setShowUpdateRepeatBooking(false);
	}, [editableBooking?.id]);

	useEffect(() => {
		if (!editableBooking?.id) {
			hydratedBookingItemsRef.current = null;
		}
	}, [editableBooking?.id]);

	// useEffect(() => {
	// 	if (!isBookingPaymentInProgress) {
	// 		setShowPaymentInProgressNotice(false);
	// 	}
	// }, [isBookingPaymentInProgress]);

	useEffect(() => () => {
		setCompleteBookingWarning(false);
		setShowPaymentInProgressNotice(false);
	}, [setCompleteBookingWarning, setShowPaymentInProgressNotice]);

	useEffect(() => {
		const errorContainerElement = overlappingErrorContainerRef.current;
		if (!errorContainerElement) {
			setOverlappingErrorContainerHeight(0);
			return undefined;
		}

		const updateContainerHeight = () => {
			const measuredHeight = Math.ceil(errorContainerElement.getBoundingClientRect().height || 0);
			setOverlappingErrorContainerHeight(measuredHeight);
		};

		updateContainerHeight();

		if (typeof ResizeObserver === "undefined") {
			return undefined;
		}

		const resizeObserver = new ResizeObserver(() => {
			updateContainerHeight();
		});

		resizeObserver.observe(errorContainerElement);

		return () => {
			resizeObserver.disconnect();
		};
	}, [
		isSubmitting,
		overlappingErrors.length,
		roomConflictErrors.length,
		apiErrorForCreateBooking,
		saveValidationError,
		isGreyBox,
	]);

	const handleRepeatBookingSave = () => {
		setRepeatBookingOptionSelected(true);
		setShowUpdateRepeatBooking(false);
		// The update_scope is already set in editedItemData by UpdateRepeatBooking component
		// User can now continue editing and click "Save Changes" to update the booking
	}

	const handleRepeatBookingCancel = () => {
		setRepeatBookingOptionSelected(false);
		setShowUpdateRepeatBooking(false);
		// Clear the update_scope from editedItemData when canceled
		setEditedItemData((prev) => {
			const { update_scope, ...rest } = prev || {};
			return rest;
		});
		// Reset the selected scope in context
		setSelectedBookingUpdateScope(null);
	}

	// HANDLE SELECT GUEST CHECKIN START
	// key: 'customer' for customer tag, or pax.id (e.g., 'P1', 'P2') for pax tags
	const handleSelectGuestCheckin = (user, key = 'customer') => {
		if (user) {
			setSelectedGuestCheckin((prev) => ({
				...prev,
				[key]: { item: user }
			}));
		} else {
			setSelectedGuestCheckin((prev) => ({
				...prev,
				[key]: { item: null, cleared: true }
			}));
		}
	};
	// HANDLE SELECT GUEST CHECKIN END

	// GET SELECTED GUEST CHECKIN FOR A SPECIFIC KEY
	const getSelectedGuestCheckin = (key = 'customer') => {
		return selectedGuestCheckin[key] || null;
	};

	const getGuestCheckinPayload = useCallback((key = 'customer') => {
		const selectedEntry = selectedGuestCheckin?.[key];
		const selected = selectedEntry?.item;
		if (!selected) {
			return {
				hasSelection: !!selectedEntry?.cleared,
				guestCheckinId: null,
				guestCheckinName: null,
				below12: 0,
			};
		}

		const below12 = isBelow12Tag(selected);
		const guestCheckinName = below12
			? null
			: `${selected.first_name || selected.name || ""} ${selected.last_name || selected.lastname || ""}`.trim() || null;

		return {
			hasSelection: true,
			guestCheckinId: below12 ? null : selected.id || null,
			guestCheckinName,
			below12: below12 ? 1 : 0,
		};
	}, [selectedGuestCheckin]);

	const parseServiceAtDate = useCallback((value) => {
		if (!value) return null;
		if (value instanceof Date) {
			return Number.isNaN(value.getTime()) ? null : value;
		}

		const normalizedValue = String(value).trim().replace("T", " ");
		const ddmmyyyyMatch = normalizedValue.match(/^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/);
		if (ddmmyyyyMatch) {
			const [, day, month, year, hour = "00", minute = "00", second = "00"] = ddmmyyyyMatch;
			const parsedDate = new Date(
				Number(year),
				Number(month) - 1,
				Number(day),
				Number(hour),
				Number(minute),
				Number(second)
			);
			return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
		}

		const parsedDate = new Date(normalizedValue);
		return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
	}, []);

	const normalizeDateString = useCallback((dateInput) => {
		if (!dateInput) return null;
		if (typeof dateInput === "string" && /^\d{2}-\d{2}-\d{4}$/.test(dateInput)) {
			return dateInput;
		}
		const parsedDate = parseServiceAtDate(dateInput);
		return parsedDate ? formatDateToDDMMYYYY(parsedDate) : null;
	}, [parseServiceAtDate]);

	const addDaysToDateString = useCallback((dateStr, days) => {
		const normalizedDate = normalizeDateString(dateStr);
		if (!normalizedDate) return null;
		const [day, month, year] = normalizedDate.split("-").map(Number);
		const nextDate = new Date(year, month - 1, day);
		nextDate.setDate(nextDate.getDate() + Number(days || 0));
		return formatDateToDDMMYYYY(nextDate);
	}, [normalizeDateString]);

	const doesRangeCrossMidnight = useCallback((startTime, endTime) => {
		const startMinutes = convertToMinutes(startTime);
		const endMinutes = convertToMinutes(endTime);
		if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) return false;
		// endMinutes === 0 (exactly "00:00") also crosses into the next day, e.g. 23:00 + 60min
		// ends at 00:00 the next day — this must not be treated as "same day".
		return endMinutes < startMinutes;
	}, []);

	const getDateOffsetFromBase = useCallback((dateInput, baseDateInput = selectedDate) => {
		const normalizedDate = normalizeDateString(dateInput);
		const normalizedBaseDate = normalizeDateString(baseDateInput);
		if (!normalizedDate || !normalizedBaseDate) return 0;
		const [dateDay, dateMonth, dateYear] = normalizedDate.split("-").map(Number);
		const [baseDay, baseMonth, baseYear] = normalizedBaseDate.split("-").map(Number);
		const resolvedDate = new Date(dateYear, dateMonth - 1, dateDay);
		const baseDate = new Date(baseYear, baseMonth - 1, baseDay);
		resolvedDate.setHours(0, 0, 0, 0);
		baseDate.setHours(0, 0, 0, 0);
		// Signed: previous day vs viewed edit date must stay negative (e.g. 16th while viewing 17th).
		return Math.round((resolvedDate.getTime() - baseDate.getTime()) / (24 * 60 * 60 * 1000));
	}, [normalizeDateString, selectedDate]);

	const buildTimelineMetaForService = useCallback((startDateStr, startTime, baseDateInput = selectedDate, explicitTimelineDayOffset = null) => {
		const normalizedStartTime = extractHHMM(startTime);
		const dateBasedOffset = getDateOffsetFromBase(startDateStr, baseDateInput);
		let dayOffset = dateBasedOffset;
		if (typeof explicitTimelineDayOffset === "number" && !Number.isNaN(explicitTimelineDayOffset)) {
			if (dateBasedOffset < 0) {
				dayOffset = dateBasedOffset;
			} else {
				dayOffset = Math.max(dateBasedOffset, explicitTimelineDayOffset);
			}
		}
		return {
			dayOffset,
			absoluteMinutes: normalizedStartTime
				? (dayOffset * 1440) + convertToMinutes(normalizedStartTime)
				: null,
		};
	}, [extractHHMM, getDateOffsetFromBase, selectedDate]);

	const emitProjectedEditOverride = useCallback((detail) => {
		try {
			window.dispatchEvent(new CustomEvent("editOverrideUpdate", {
				detail: buildProjectedOverridePayload(detail),
			}));
		} catch {}
	}, []);

	const logEditPayloadDebug = useCallback(() => {}, []);

	const resolvedEditDate = selectedDate || editBooking?.date || null;

	const getFirstMainServiceItemId = useCallback(() => {
		const first = Array.isArray(selectedServices) && selectedServices.length > 0 ? selectedServices[0] : null;
		const id = first?.booking_item_id ?? first?.item_id;
		return id != null ? String(id) : null;
	}, [selectedServices, selectedServices?.[0]?.booking_item_id, selectedServices?.[0]?.item_id]);

	const isPrefillForFirstMainService = useCallback((currentPrefill) => {
		if (!currentPrefill?.itemId) return false;
		const firstId = getFirstMainServiceItemId();
		return firstId != null && String(currentPrefill.itemId) === firstId;
	}, [getFirstMainServiceItemId]);

	const buildHeaderTimeFromService = useCallback((serviceLike) => {
		if (!serviceLike?.start_time) return null;
		const dayOffset = typeof serviceLike?.dayOffset === "number" && !Number.isNaN(serviceLike.dayOffset)? Math.max(0, serviceLike.dayOffset): (typeof serviceLike?.absoluteMinutes === "number" && !Number.isNaN(serviceLike.absoluteMinutes)? Math.max(0, Math.floor(serviceLike.absoluteMinutes / 1440)): 0);
		const absoluteMinutes = typeof serviceLike?.absoluteMinutes === "number" && !Number.isNaN(serviceLike.absoluteMinutes)? serviceLike.absoluteMinutes: (dayOffset * 1440) + convertToMinutes(serviceLike.start_time);
		return buildTimeSelection(serviceLike.start_time,dayOffset,convertTo12Hour(serviceLike.start_time),absoluteMinutes);
	}, []);

	const buildHeaderTimeFromPrefill = useCallback((currentPrefill) => {
		if (!currentPrefill?.startTime) return null;
		const absoluteMinutes =typeof currentPrefill.absoluteStartMinutes === "number" && !Number.isNaN(currentPrefill.absoluteStartMinutes)? currentPrefill.absoluteStartMinutes: null;
		const dayOffset = absoluteMinutes != null ? Math.max(0, Math.floor(absoluteMinutes / 1440)): 0;
		return buildTimeSelection(currentPrefill.startTime,dayOffset,convertTo12Hour(currentPrefill.startTime),absoluteMinutes ?? (dayOffset * 1440) + convertToMinutes(currentPrefill.startTime)
		);
	}, []);

	const applyHeaderTimeSelection = useCallback((normalizedTime) => {
		if (!normalizedTime) return;
		setSelectedTime((prev) => {
			if (prev?.value === normalizedTime.value && prev?.label === normalizedTime.label && prev?.dayOffset === normalizedTime.dayOffset && prev?.absoluteMinutes === normalizedTime.absoluteMinutes) {
				return prev;
			}
			return normalizedTime;
		});
		setEditBooking((prev) => {
			if (prev?.time?.value === normalizedTime.value && prev?.time?.label === normalizedTime.label && prev?.time?.dayOffset === normalizedTime.dayOffset && prev?.time?.absoluteMinutes === normalizedTime.absoluteMinutes) {
				return prev;
			}
			return {
				...prev,
				time: normalizedTime,
			};
		});
	}, [setSelectedTime]);

	// Keep edit header time aligned when services change (e.g. calendar drag prefill) or dropdown picks time.
	const lastServiceHeaderSyncSigRef = useRef(null);
	const lastPrefillHeaderSyncSigRef = useRef(null);

	useEffect(() => {
		const firstService = Array.isArray(selectedServices) && selectedServices.length > 0 ? selectedServices[0] : null;
		const firstServiceStartTime = firstService?.start_time || null;
		if (!firstServiceStartTime) return;

		// Wait until first-service drag prefill is applied before syncing from stale rows.
		if (prefill?.startTime &&isPrefillForFirstMainService(prefill) &&firstServiceStartTime !== prefill.startTime) {return;}
		const serviceSig = `${firstServiceStartTime}:${firstService?.dayOffset ?? ""}:${firstService?.absoluteMinutes ?? ""}`;
		if (lastServiceHeaderSyncSigRef.current === serviceSig) {return;}

		const syncedFromService = buildHeaderTimeFromService(firstService);
		if (!syncedFromService) return;

		lastServiceHeaderSyncSigRef.current = serviceSig;
		applyHeaderTimeSelection(syncedFromService);
	}, [selectedServices,selectedServices?.[0]?.start_time, selectedServices?.[0]?.dayOffset,selectedServices?.[0]?.absoluteMinutes,prefill?.startTime,prefill?.itemId,isPrefillForFirstMainService,buildHeaderTimeFromService,applyHeaderTimeSelection,]);

	// Sync header immediately when calendar drag supplies editPrefill for the first main service.
	useEffect(() => {
		if (!prefill?.startTime) return;
		if (!isPrefillForFirstMainService(prefill)) return;

		const prefillSig = `${prefill.startTime}:${prefill.absoluteStartMinutes ?? ""}`;
		if (lastPrefillHeaderSyncSigRef.current === prefillSig) {return;}

		const normalizedTime = buildHeaderTimeFromPrefill(prefill);
		if (!normalizedTime) return;

		lastPrefillHeaderSyncSigRef.current = prefillSig;
		applyHeaderTimeSelection(normalizedTime);
	}, [prefill?.startTime,prefill?.absoluteStartMinutes,prefill?.itemId,selectedServices?.[0]?.booking_item_id,selectedServices?.[0]?.item_id,isPrefillForFirstMainService,buildHeaderTimeFromPrefill,applyHeaderTimeSelection,]);

	const fetchGuestCheckinListPage = useCallback(async ({ page = 1, search = "", append = false } = {}) => {
		if (append) {
			if (guestCheckinAppendInFlightRef.current) return;
			guestCheckinAppendInFlightRef.current = true;
		}
		const reqId = ++guestCheckinListRequestIdRef.current;
		const normalizedSearch = (search || "").trim();
		lastGuestCheckinSearchRef.current = normalizedSearch;

		const normalizedBase = normalizeDateString(resolvedEditDate);
		const extendedDayOffset = getTimeSelectionDayOffset(editBooking?.time);
		const serviceAtDateStr =
			normalizedBase && extendedDayOffset > 0
				? addDaysToDateString(normalizedBase, extendedDayOffset) || normalizedBase
				: normalizedBase;

		const params = {
			pagination: 1,
			page,
			outlet: selectedOutlet,
			search_text: normalizedSearch,
			...(resolvedEditDate ? {
				service_at: `${serviceAtDateStr || formatDateToDDMMYYYY(resolvedEditDate)} ${editBooking?.time?.value || ""}`,
			} : {}),
		};

		if (append) {
			setGuestCheckinLoadingMore(true);
		}

		try {
			const response = await getGuestCheckinList(params);
			if (reqId !== guestCheckinListRequestIdRef.current) return;
			if (response?.data?.success) {
				const list = response.data.data.list;
				const users = list?.users ?? [];
				const pagination = list?.pagination ?? null;
				if (append) {
					setGuestCheckinList((prev) => [...prev, ...users]);
				} else {
					setGuestCheckinList(users);
				}
				setGuestCheckinPagination(pagination);
			} else if (!append) {
				console.error("Error fetching guest checkin list:", response);
			}
		} catch (error) {
			if (reqId !== guestCheckinListRequestIdRef.current) return;
			console.error("Error fetching guest checkin list:", error);
		} finally {
			if (append) {
				guestCheckinAppendInFlightRef.current = false;
				setGuestCheckinLoadingMore(false);
			}
		}
	}, [
		resolvedEditDate,
		selectedOutlet,
		editBooking?.time,
		normalizeDateString,
		addDaysToDateString,
		formatDateToDDMMYYYY,
		getTimeSelectionDayOffset,
		setGuestCheckinList,
	]);

	const scheduleGuestCheckinSearch = useCallback((query) => {
		if (guestCheckinSearchDebounceRef.current) {
			clearTimeout(guestCheckinSearchDebounceRef.current);
		}
		guestCheckinSearchDebounceRef.current = setTimeout(() => {
			guestCheckinSearchDebounceRef.current = null;
			fetchGuestCheckinListPage({ page: 1, search: query, append: false });
		}, 400);
	}, [fetchGuestCheckinListPage]);

	const handleGetGuestCheckinList = useCallback((isOpen) => {
		if (guestCheckinSearchDebounceRef.current) {
			clearTimeout(guestCheckinSearchDebounceRef.current);
			guestCheckinSearchDebounceRef.current = null;
		}
		if (isOpen) {
			fetchGuestCheckinListPage({ page: 1, search: "", append: false });
		}
	}, [fetchGuestCheckinListPage]);

	const handleGuestCheckinLoadMore = useCallback(() => {
		const p = guestCheckinPagination;
		if (!p || guestCheckinLoadingMore) return;
		const next = (p.currentPage ?? 0) + 1;
		const last = p.lastPage ?? 1;
		if (next > last) return;
		fetchGuestCheckinListPage({
			page: next,
			search: lastGuestCheckinSearchRef.current,
			append: true,
		});
	}, [guestCheckinPagination, guestCheckinLoadingMore, fetchGuestCheckinListPage]);

	const guestCheckinHasMore =
		guestCheckinPagination != null &&
		guestCheckinPagination.currentPage < guestCheckinPagination.lastPage;

	useEffect(() => {
		return () => {
			if (guestCheckinSearchDebounceRef.current) {
				clearTimeout(guestCheckinSearchDebounceRef.current);
			}
		};
	}, []);

	const buildDateRangeFromResolvedStartDate = useCallback((startDateStr, startTime, endTime) => {
		const normalizedStartDate = normalizeDateString(startDateStr);
		if (!normalizedStartDate) {
			return { start_date: null, end_date: null };
		}
		return {
			start_date: normalizedStartDate,
			end_date: doesRangeCrossMidnight(startTime, endTime)
				? addDaysToDateString(normalizedStartDate, 1)
				: normalizedStartDate,
		};
	}, [addDaysToDateString, doesRangeCrossMidnight, normalizeDateString]);

	const deriveDateRangeFromSelection = useCallback((baseDateInput, startTime, endTime, explicitDayOffset = 0) => {
		const normalizedBaseDate = normalizeDateString(baseDateInput);
		if (!normalizedBaseDate) {
			return { start_date: null, end_date: null };
		}
		const resolvedStartDate = addDaysToDateString(normalizedBaseDate, explicitDayOffset);
		return buildDateRangeFromResolvedStartDate(resolvedStartDate, startTime, endTime);
	}, [addDaysToDateString, buildDateRangeFromResolvedStartDate, normalizeDateString]);

	const extendedBusinessCutoffMinutes = 6 * 60;

	const getEditBaseDateStr = useCallback(() => (
		normalizeDateString(resolvedEditDate)
	), [normalizeDateString, resolvedEditDate]);

	const isExtendedBoundaryCrossDayService = useCallback((serviceLike, baseDateStr = getEditBaseDateStr()) => {
		if (!isOutletExtended) return false;
		const startDate = serviceLike?.start_date || null;
		const startTime = serviceLike?.start_time || null;
		if (!startDate || !startTime) return false;
		const startMinutes = convertToMinutes(startTime);
		if (Number.isNaN(startMinutes) || startMinutes < extendedBusinessCutoffMinutes) return false;
		return getDateOffsetFromBase(startDate, baseDateStr) > 0 || (baseDateStr ? startDate !== baseDateStr : false);
	}, [getDateOffsetFromBase, getEditBaseDateStr, isOutletExtended]);

	const isMixedNormalAndExtendedCrossDayService = useCallback((startDate, endDate, startTime, endTime) => {
		if (!isOutletExtended) return false;
		if (!startDate || !endDate || startDate === endDate) return false;
		const startMinutes = convertToMinutes(startTime);
		const endMinutes = convertToMinutes(endTime);
		if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) return false;
		// endMinutes === 0 means the service ends exactly at midnight — zero real duration on
		// end_date, so it's a boundary rollover, not a genuine split with presence on both days.
		if (endMinutes === 0) return false;
		return startMinutes >= extendedBusinessCutoffMinutes && endMinutes < extendedBusinessCutoffMinutes;
	}, [extendedBusinessCutoffMinutes, isOutletExtended]);

	const buildMidnightSplitCrossDayItems = useCallback((startDate, endDate, startTime, endTime) => {
		const startMinutes = convertToMinutes(startTime);
		const endMinutes = convertToMinutes(endTime);
		if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) {
			return null;
		}
		return [
			{
				start_time: startTime,
				end_time: "00:00",
				duration: (24 * 60) - startMinutes,
				start_date: startDate,
				end_date: startDate,
			},
			{
				start_time: "00:00",
				end_time: endTime,
				duration: endMinutes,
				start_date: endDate,
				end_date: endDate,
			},
		];
	}, []);

	const isPureExtendedCoverageService = useCallback((startDate, endDate, startTime, endTime) => {
		if (!startDate || !endDate || startDate !== endDate) return false;
		const startMinutes = convertToMinutes(startTime);
		const endMinutes = convertToMinutes(endTime);
		if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) return false;
		return startMinutes >= 0 &&
			startMinutes < extendedBusinessCutoffMinutes &&
			endMinutes > startMinutes &&
			endMinutes <= extendedBusinessCutoffMinutes;
	}, [extendedBusinessCutoffMinutes]);

	const isEarlyMorningBoundaryTimeRange = useCallback((startTime, endTime) => {
		const startMinutes = convertToMinutes(startTime);
		const endMinutes = convertToMinutes(endTime);
		if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) return false;
		return startMinutes >= 0 &&
			startMinutes < extendedBusinessCutoffMinutes &&
			endMinutes > extendedBusinessCutoffMinutes;
	}, [extendedBusinessCutoffMinutes]);

	const isEarlyMorningBoundarySplitService = useCallback((startDate, endDate, startTime, endTime) => {
		if (!startDate || !endDate || startDate !== endDate) return false;
		return isEarlyMorningBoundaryTimeRange(startTime, endTime);
	}, [isEarlyMorningBoundaryTimeRange]);

	const normalizeEarlyMorningPayloadDateRange = useCallback((startDate, endDate, startTime, endTime) => {
		if (!isEarlyMorningBoundaryTimeRange(startTime, endTime)) {
			return { start_date: startDate, end_date: endDate };
		}
		const sameDate = endDate || startDate;
		return { start_date: sameDate, end_date: sameDate };
	}, [isEarlyMorningBoundaryTimeRange]);

	const buildEarlyMorningBoundarySplitItems = useCallback((actualDate, startTime, endTime) => {
		if (!actualDate) return null;
		const startMinutes = convertToMinutes(startTime);
		const endMinutes = convertToMinutes(endTime);
		if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) {
			return null;
		}
		const coveredDate = addDaysToDateString(actualDate, -1);
		return [
			{
				start_time: startTime,
				end_time: "06:00",
				duration: extendedBusinessCutoffMinutes - startMinutes,
				start_date: coveredDate,
				end_date: coveredDate,
			},
			{
				start_time: "06:00",
				end_time: endTime,
				duration: endMinutes - extendedBusinessCutoffMinutes,
				start_date: actualDate,
				end_date: actualDate,
			},
		];
	}, [addDaysToDateString, extendedBusinessCutoffMinutes]);

	const hasExpandedCoverageSegments = useCallback((serviceLike) => {
		const segments = Array.isArray(serviceLike?.cross_day_booking_item)
			? serviceLike.cross_day_booking_item
			: [];
		if (segments.length < 2) return false;
		const firstStart = segments[0]?.start_time || null;
		const firstEnd = segments[0]?.end_time || null;
		const allSameTimes = segments.every((segment) =>
			(segment?.start_time || null) === firstStart &&
			(segment?.end_time || null) === firstEnd
		);
		if (!allSameTimes) return false;
		const uniqueDates = new Set(segments.map((segment) => segment?.start_date || null).filter(Boolean));
		return uniqueDates.size >= 2;
	}, []);

	const hasDegenerateZeroDurationSegment = useCallback((segments) => {
		if (!Array.isArray(segments) || segments.length === 0) return false;
		return segments.some((segment) => {
			const segStart = convertToMinutes(extractHHMM(segment?.start_time || ""));
			const segEnd = convertToMinutes(extractHHMM(segment?.end_time || ""));
			if (Number.isNaN(segStart) || Number.isNaN(segEnd)) return false;
			return segStart === segEnd;
		});
	}, [extractHHMM]);

	// 06:00 outlet boundary split across two dates (shows on both days in Home). This must ONLY
	// match a genuine split (different clock times per segment). The old "identical clock window
	// duplicated across two dates" shape is not a real split and must never be recognized here,
	// otherwise it would force is_calendar_expansion back to 1 for a non-split booking.
	const isDualCalendarDisplayCrossDayForEditPayload = useCallback((segments) => {
		if (!Array.isArray(segments) || segments.length < 2) return false;
		const s0 = segments[0];
		const s1 = segments[1];
		const firstStartM = convertToMinutes(extractHHMM(s0?.start_time || ""));
		const firstEndM = convertToMinutes(extractHHMM(s0?.end_time || ""));
		const secondStartM = convertToMinutes(extractHHMM(s1?.start_time || ""));
		const secondEndM = convertToMinutes(extractHHMM(s1?.end_time || ""));
		if (
			Number.isNaN(firstStartM) ||
			Number.isNaN(firstEndM) ||
			Number.isNaN(secondStartM) ||
			Number.isNaN(secondEndM)
		) {
			return false;
		}
		const d0 = normalizeDateString(s0?.start_date);
		const d1 = normalizeDateString(s1?.start_date);
		if (!d0 || !d1 || d0 === d1) return false;

		if (firstEndM === 0 || secondStartM === 0) {
			return false;
		}
		if (firstStartM >= extendedBusinessCutoffMinutes) {
			return false;
		}
		if (firstEndM !== extendedBusinessCutoffMinutes || secondStartM !== extendedBusinessCutoffMinutes) {
			return false;
		}
		return true;
	}, [extendedBusinessCutoffMinutes, extractHHMM, normalizeDateString]);

	const getSelectedDayCrossDaySegmentPosition = useCallback((serviceLike, baseDateInput = resolvedEditDate) => {
		const normalizedBaseDate = normalizeDateString(baseDateInput);
		if (!normalizedBaseDate) return null;
		const segments = Array.isArray(serviceLike?.cross_day_booking_item)
			? serviceLike.cross_day_booking_item
			: [];
		if (segments.length < 2) return null;
		const firstStartMinutes = convertToMinutes(extractHHMM(segments[0]?.start_time || ""));
		if (Number.isNaN(firstStartMinutes) || firstStartMinutes >= extendedBusinessCutoffMinutes) {
			return null;
		}
		const firstDate = normalizeDateString(segments[0]?.start_date);
		const secondDate = normalizeDateString(segments[1]?.start_date);
		if (firstDate === normalizedBaseDate) return "first";
		if (secondDate === normalizedBaseDate) return "second";
		return null;
	}, [extendedBusinessCutoffMinutes, extractHHMM, normalizeDateString, resolvedEditDate]);

	const buildPayloadCrossDayBookingItem = useCallback((startDate, endDate, startTime, endTime) => {
		if (!startDate || !endDate) {
			return null;
		}

		const startMinutes = convertToMinutes(startTime);
		const endMinutes = convertToMinutes(endTime);
		if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) {
			return null;
		}

		if (isEarlyMorningBoundaryTimeRange(startTime, endTime)) {
			return null;
		}

		if (isMixedNormalAndExtendedCrossDayService(startDate, endDate, startTime, endTime)) {
			return buildMidnightSplitCrossDayItems(startDate, endDate, startTime, endTime);
		}

		if (isOutletExtended && startDate === endDate) {
			if (!isExtendedBoundaryCrossDayService({
				start_date: startDate,
				end_date: endDate,
				start_time: startTime,
			})) {
				return null;
			}

			const singleSegmentDuration = endMinutes > startMinutes
				? endMinutes - startMinutes
				: (endMinutes + (24 * 60)) - startMinutes;

			return [{
				start_time: startTime,
				end_time: endTime,
				duration: singleSegmentDuration,
				start_date: startDate,
				end_date: endDate,
			}];
		}

		if (startDate === endDate) {
			return null;
		}

		// A service ending exactly at "00:00" has zero real duration on end_date — it's just the
		// date rolling over at the boundary, not a genuine split with presence on both days.
		if (endMinutes === 0) {
			return null;
		}

		if (!isOutletExtended) {
			return [
				{
					start_time: startTime,
					end_time: "00:00",
					duration: (24 * 60) - startMinutes,
					start_date: startDate,
					end_date: startDate,
				},
				{
					start_time: "00:00",
					end_time: endTime,
					duration: endMinutes,
					start_date: endDate,
					end_date: endDate,
				},
			];
		}

		const boundaryAbsoluteMinutes = 1440 + extendedBusinessCutoffMinutes;
		const startAbsoluteMinutes = startMinutes < extendedBusinessCutoffMinutes
			? 1440 + startMinutes
			: startMinutes;
		let endAbsoluteMinutes = endMinutes < extendedBusinessCutoffMinutes
			? 1440 + endMinutes
			: endMinutes;

		if (endAbsoluteMinutes <= startAbsoluteMinutes) {
			endAbsoluteMinutes += 1440;
		}

		if (
			startAbsoluteMinutes >= boundaryAbsoluteMinutes ||
			endAbsoluteMinutes <= boundaryAbsoluteMinutes
		) {
			return null;
		}

		return [
			{
				start_time: startTime,
				end_time: "06:00",
				duration: boundaryAbsoluteMinutes - startAbsoluteMinutes,
				start_date: startDate,
				end_date: startDate,
			},
			{
				start_time: "06:00",
				end_time: endTime,
				duration: endAbsoluteMinutes - boundaryAbsoluteMinutes,
				start_date: endDate,
				end_date: endDate,
			},
		];
	}, [buildMidnightSplitCrossDayItems, extendedBusinessCutoffMinutes, isEarlyMorningBoundaryTimeRange, isExtendedBoundaryCrossDayService, isMixedNormalAndExtendedCrossDayService, isOutletExtended]);

	const getItemCalendarExpansionFlag = useCallback((serviceLike, startDateCandidate = null) => {
		if (
			serviceLike?.is_calendar_expansion === 1 ||
			serviceLike?.is_calendar_expansion === "1" ||
			serviceLike?.is_calendar_expansion === true
		) {
			return 1;
		}
		if (
			serviceLike?.is_calendar_expansion === 0 ||
			serviceLike?.is_calendar_expansion === "0" ||
			serviceLike?.is_calendar_expansion === false
		) {
			return 0;
		}
		if (typeof serviceLike?.dayOffset === "number" && serviceLike.dayOffset > 0) {
			return 1;
		}
		if (typeof serviceLike?.absoluteMinutes === "number" && serviceLike.absoluteMinutes >= 1440) {
			return 1;
		}
		return getDateOffsetFromBase(startDateCandidate || serviceLike?.start_date || null) > 0 ? 1 : 0;
	}, [getDateOffsetFromBase]);

	const isActualSplitBookingService = useCallback((serviceLike) => {
		const segments = Array.isArray(serviceLike?.cross_day_booking_item)
			? serviceLike.cross_day_booking_item
			: [];
		if (segments.length < 2) return false;
		if (getItemCalendarExpansionFlag(serviceLike, serviceLike?.start_date) === 1) {
			return false;
		}
		const uniqueDates = new Set(
			segments.map((segment) => normalizeDateString(segment?.start_date)).filter(Boolean)
		);
		if (uniqueDates.size < 2) {
			return false;
		}
		const firstStart = extractHHMM(segments[0]?.start_time || "");
		const firstEnd = extractHHMM(segments[0]?.end_time || "");
		return segments.some((segment) => (
			extractHHMM(segment?.start_time || "") !== firstStart ||
			extractHHMM(segment?.end_time || "") !== firstEnd
		));
	}, [extractHHMM, getItemCalendarExpansionFlag, normalizeDateString]);

	const getResolvedServiceDayOffset = useCallback((serviceLike, startDateCandidate = null, startTimeCandidate = null) => {
		if (typeof serviceLike?.dayOffset === "number" && !Number.isNaN(serviceLike.dayOffset)) {
			return serviceLike.dayOffset;
		}
		if (typeof serviceLike?.absoluteMinutes === "number" && !Number.isNaN(serviceLike.absoluteMinutes)) {
			return Math.floor(serviceLike.absoluteMinutes / 1440);
		}

		const crossDaySegmentPosition = getSelectedDayCrossDaySegmentPosition(
			serviceLike,
			resolvedEditDate
		);
		if (crossDaySegmentPosition === "first") {
			return 1;
		}
		if (crossDaySegmentPosition === "second") {
			return 0;
		}

		const explicitExpansionFlag = getItemCalendarExpansionFlag(serviceLike, startDateCandidate);
		const normalizedStartTime = extractHHMM(startTimeCandidate || serviceLike?.start_time || "");
		const startMinutes = convertToMinutes(normalizedStartTime);

		if (
			isOutletExtended &&
			explicitExpansionFlag === 1 &&
			!hasExpandedCoverageSegments(serviceLike) &&
			!isEarlyMorningBoundarySplitService(
				startDateCandidate || serviceLike?.start_date || null,
				serviceLike?.end_date || startDateCandidate || serviceLike?.start_date || null,
				normalizedStartTime,
				extractHHMM(serviceLike?.end_time || "")
			) &&
			!isPureExtendedCoverageService(
				startDateCandidate || serviceLike?.start_date || null,
				serviceLike?.end_date || startDateCandidate || serviceLike?.start_date || null,
				normalizedStartTime,
				extractHHMM(serviceLike?.end_time || "")
			) &&
			!Number.isNaN(startMinutes) &&
			startMinutes < extendedBusinessCutoffMinutes
		) {
			return 1;
		}

		return getDateOffsetFromBase(startDateCandidate || serviceLike?.start_date || null);
	}, [
		extendedBusinessCutoffMinutes,
		extractHHMM,
		getSelectedDayCrossDaySegmentPosition,
		getDateOffsetFromBase,
		getItemCalendarExpansionFlag,
		hasExpandedCoverageSegments,
		isEarlyMorningBoundarySplitService,
		isPureExtendedCoverageService,
		isOutletExtended,
	]);

	const resolvePayloadDateRangeForService = useCallback((
		serviceLike,
		startTime,
		endTime,
		sequentialBaseDate = null
	) => {
		const normalizedServiceStartDate = normalizeDateString(serviceLike?.start_date);
		const normalizedServiceEndDate = normalizeDateString(serviceLike?.end_date);
		const normalizedResolvedEditBaseDate = normalizeDateString(resolvedEditDate);
		const normalizedSequentialBaseDate = normalizeDateString(sequentialBaseDate);
		const hasExplicitTimelineMeta =
			(typeof serviceLike?.dayOffset === "number" && !Number.isNaN(serviceLike.dayOffset)) ||
			(typeof serviceLike?.absoluteMinutes === "number" && !Number.isNaN(serviceLike.absoluteMinutes));

		if (hasExplicitTimelineMeta && normalizedResolvedEditBaseDate) {
			const explicitDayOffset = getResolvedServiceDayOffset(
				serviceLike,
				normalizedServiceStartDate,
				startTime
			);
			const explicitStartDate = addDaysToDateString(normalizedResolvedEditBaseDate, explicitDayOffset);
			return buildDateRangeFromResolvedStartDate(explicitStartDate, startTime, endTime);
		}

		if (normalizedServiceStartDate) {
			return buildDateRangeFromResolvedStartDate(normalizedServiceStartDate, startTime, endTime);
		}

		if (normalizedSequentialBaseDate && startTime && endTime) {
			return deriveDateRangeFromSelection(normalizedSequentialBaseDate, startTime, endTime, 0);
		}

		return {
			start_date: normalizedServiceStartDate,
			end_date: normalizedServiceEndDate ?? normalizedServiceStartDate,
		};
	}, [
		addDaysToDateString,
		buildDateRangeFromResolvedStartDate,
		deriveDateRangeFromSelection,
		getResolvedServiceDayOffset,
		normalizeDateString,
		resolvedEditDate,
	]);

	const getPayloadDateRangeForEditSave = useCallback((
		serviceLike,
		baseServiceLike,
		startTime,
		endTimeCalc,
		sequentialBaseDate
	) => {
		const normSegDate = (s) => normalizeDateString(s?.start_date);
		// Same clock window on two calendar days (expanded coverage). Never trust service_at alone:
		// API often stores the first segment's calendar date on service_at (e.g. 21 May 00:30) even
		// when the real slot after a chained overnight service is the next day (22 May).
		if (hasExpandedCoverageSegments(serviceLike)) {
			const segments = serviceLike.cross_day_booking_item;
			const normalizedSequential = normalizeDateString(sequentialBaseDate);
			const normalizedEdit = normalizeDateString(resolvedEditDate);
			const matched =
				(normalizedSequential ? segments.find((s) => normSegDate(s) === normalizedSequential) : null)
				|| (normalizedEdit ? segments.find((s) => normSegDate(s) === normalizedEdit) : null)
				|| segments[segments.length - 1];
			const actualDateStr = normSegDate(matched);
			const stHH = extractHHMM(startTime);
			const etHH = extractHHMM(endTimeCalc);
			if (
				actualDateStr &&
				stHH &&
				etHH &&
				isPureExtendedCoverageService(actualDateStr, actualDateStr, stHH, etHH)
			) {
				return {
					start_date: actualDateStr,
					end_date: actualDateStr,
				};
			}
		}

		const rawServiceAt =
			(typeof serviceLike?.service_at === "string" && String(serviceLike.service_at).trim())
				? String(serviceLike.service_at).trim().replace("T", " ")
				: (typeof baseServiceLike?.service_at === "string" && String(baseServiceLike.service_at).trim()
					? String(baseServiceLike.service_at).trim().replace("T", " ")
					: null);

		const persistedId = serviceLike?.booking_item_id ?? serviceLike?.item_id;
		if (rawServiceAt && persistedId != null) {
			const parts = rawServiceAt.split(/\s+/);
			const timePart = parts.length > 1 ? parts[parts.length - 1] : "";
			const anchorTime = extractHHMM(timePart);
			if (anchorTime && extractHHMM(startTime) === anchorTime) {
				const parsedAt = parseServiceAtDate(rawServiceAt);
				const anchorDateStr = parsedAt ? formatDateToDDMMYYYY(parsedAt) : null;
				if (anchorDateStr) {
					return buildDateRangeFromResolvedStartDate(anchorDateStr, startTime, endTimeCalc);
				}
			}
		}

		return resolvePayloadDateRangeForService(
			serviceLike,
			startTime,
			endTimeCalc,
			sequentialBaseDate
		);
	}, [
		buildDateRangeFromResolvedStartDate,
		extractHHMM,
		formatDateToDDMMYYYY,
		hasExpandedCoverageSegments,
		isPureExtendedCoverageService,
		normalizeDateString,
		parseServiceAtDate,
		resolvedEditDate,
		resolvePayloadDateRangeForService,
	]);

	const getSegmentDateRange = useCallback((segments) => {
		if (!Array.isArray(segments) || segments.length === 0) return null;
		const normalizedDates = segments
			.map((segment) => normalizeDateString(segment?.start_date))
			.filter(Boolean);
		if (normalizedDates.length < 2) return null;
		return {
			start_date: normalizedDates[0],
			end_date: normalizedDates[normalizedDates.length - 1],
		};
	}, [normalizeDateString]);

	const getPrefillOverrides = (currentPrefill) => {
		const baseOverrides =
			currentPrefill?.bookingOverrides && typeof currentPrefill.bookingOverrides === "object"
				? currentPrefill.bookingOverrides
				: {};

		if (currentPrefill?.itemId == null) {
			return baseOverrides;
		}

					return {
			...baseOverrides,
			[currentPrefill.itemId]: {
				...(baseOverrides[currentPrefill.itemId] || {}),
				bookingId: currentPrefill.bookingId,
				start: currentPrefill.startTime ?? baseOverrides[currentPrefill.itemId]?.start,
				end: currentPrefill.endTime ?? baseOverrides[currentPrefill.itemId]?.end,
				therapistId: currentPrefill.therapistId ?? baseOverrides[currentPrefill.itemId]?.therapistId,
				therapistName: currentPrefill.therapistName ?? baseOverrides[currentPrefill.itemId]?.therapistName,
				roomId: currentPrefill.roomId ?? baseOverrides[currentPrefill.itemId]?.roomId,
				roomName:
					currentPrefill.roomName ??
					currentPrefill.roomData?.room_name ??
					baseOverrides[currentPrefill.itemId]?.roomName,
				service_at: currentPrefill.service_at ?? baseOverrides[currentPrefill.itemId]?.service_at,
				linkedToPrevious:
					currentPrefill.linkedToPrevious ??
					baseOverrides[currentPrefill.itemId]?.linkedToPrevious,
				scheduleLinkParentKey: currentPrefill.scheduleLinkParentKey ?? baseOverrides[currentPrefill.itemId]?.scheduleLinkParentKey,
				absoluteStartMinutes:
					currentPrefill.absoluteStartMinutes ??
					baseOverrides[currentPrefill.itemId]?.absoluteStartMinutes,
			},
		};
	};

	const getServiceOverride = (bookingItemId, currentPrefill) => {
		if (bookingItemId == null) return null;
		const allOverrides = getPrefillOverrides(currentPrefill);
		return allOverrides?.[bookingItemId] || allOverrides?.[String(bookingItemId)] || null;
	};

	const buildSelectedTherapistOption = (therapistId, therapistName, serviceLike = null) => {
		if (therapistId == null) return null;
		const therapistFromList = Array.isArray(homeStaffList)
			? homeStaffList.find((staff) => Number(staff.id) === Number(therapistId))
			: null;

		return {
			value: Number(therapistId),
			label: therapistName || therapistFromList?.name || therapistFromList?.alias || "",
			pager:therapistFromList?.pager_number || serviceLike?.therapist_pager_number || "",
			gender: therapistFromList?.gender || "",
		};
	};

	// Drag prefill that was active when the user explicitly changed the booking date.
	// Its date (service_at / absolute minutes) and therapist belong to the old day, so they
	// must not be re-applied; the date sync owns those after a date change.
	const prefillBeforeDateChangeRef = useRef(null);
	const manualTimeSyncRef = useRef(null);

	const syncEditPrefillFromManualTimes = useCallback((services) => {
		const manual = (Array.isArray(services) ? services : []).filter(
			(svc) => svc?.hasManualTimeSelection === true && svc?.booking_item_id != null
		);
		if (manual.length === 0) return;
		setEditPrefill((prev) => {
			if (!prev) return prev;
			const nextOverrides = { ...(prev.bookingOverrides || {}) };
			manual.forEach((svc) => {
				const id = svc.booking_item_id;
				const existing = nextOverrides[id] || nextOverrides[String(id)] || {};
				const patched = {...existing,start: svc.start_time,end: svc.end_time,service_at: svc.start_date ? `${svc.start_date} ${svc.start_time}` : existing.service_at,absoluteStartMinutes:typeof svc.absoluteMinutes === "number" && !Number.isNaN(svc.absoluteMinutes)? svc.absoluteMinutes: existing.absoluteStartMinutes,};
				nextOverrides[id] = patched;
				nextOverrides[String(id)] = patched;
			});
			const moved = manual.find((svc) => String(svc.booking_item_id) === String(prev.itemId));
			return {...prev,bookingOverrides: nextOverrides,...(moved? {	startTime: moved.start_time ?? prev.startTime, endTime: moved.end_time ?? prev.endTime,absoluteStartMinutes:typeof moved.absoluteMinutes === "number" && !Number.isNaN(moved.absoluteMinutes)? moved.absoluteMinutes: prev.absoluteStartMinutes,service_at: moved.start_date ? `${moved.start_date} ${moved.start_time}` : prev.service_at,}: {}),};
		});
	}, [setEditPrefill]);

	const applyOverrideToService = (service, currentPrefill) => {
		const rawOverride = getServiceOverride(service?.booking_item_id, currentPrefill);
		if (!rawOverride) {
			return service;
		}
		const override = currentPrefill != null && currentPrefill === prefillBeforeDateChangeRef.current? {...rawOverride,service_at: null,absoluteStartMinutes: null,therapistId: null,therapistName: null,}: rawOverride;

		const overrideStartTime = override.start || service?.start_time || "";
		const overrideEndTime =
			override.end ||
			service?.end_time ||
			(overrideStartTime && service?.duration ? calculateEndTime(overrideStartTime, service.duration) : "");
		const overrideServiceAtDate = override?.service_at ? parseServiceAtDate(override.service_at) : null;
		const overrideStartDate = overrideServiceAtDate
			? formatDateToDDMMYYYY(overrideServiceAtDate)
			: service?.start_date;
		const overrideRange = buildDateRangeFromResolvedStartDate(overrideStartDate, overrideStartTime, overrideEndTime);
		const overrideTimelineDayOffset = typeof override?.absoluteStartMinutes === "number" && !Number.isNaN(override.absoluteStartMinutes)
			? Math.floor(override.absoluteStartMinutes / 1440)
			: (typeof service?.dayOffset === "number" && !Number.isNaN(service.dayOffset)
				? service.dayOffset
				: (typeof service?.absoluteMinutes === "number" && !Number.isNaN(service.absoluteMinutes)
					? Math.floor(service.absoluteMinutes / 1440)
					: null));
		const overrideTimeline = buildTimelineMetaForService(
			overrideRange.start_date,
			overrideStartTime,
			selectedDate,
			overrideTimelineDayOffset
		);
		const overrideCrossDayBookingItem =
			override?.service_at && overrideRange.start_date && overrideRange.end_date
				? buildPayloadCrossDayBookingItem(overrideRange.start_date,overrideRange.end_date,overrideStartTime,overrideEndTime): service?.cross_day_booking_item;

		// After calendar drag, prefill still holds the drop therapist/room. If the user then
		// picks another value in the dropdown, keep that selection for Save.
		// Same for time: header or service-time picks must not be replaced by the drop snapshot.
		const keepManualTherapistSelection = service?.hasManualTherapistSelection === true;
		const keepManualRoomSelection = service?.hasManualRoomSelection === true;
		const keepManualTimeSelection = service?.hasManualTimeSelection === true;

		return {
			...service,
			...(!keepManualTimeSelection && override.start ? { start_time: override.start } : {}),
			...(!keepManualTimeSelection && override.end ? { end_time: override.end } : {}),
			...(!keepManualTherapistSelection && override.therapistId != null ? { therapist: Number(override.therapistId) }: {}),
			...(!keepManualTherapistSelection && override.therapistName ? { therapist_name: override.therapistName }: {}),
			...(!keepManualTherapistSelection && override.therapistId != null
				? { selected_therapist: buildSelectedTherapistOption(override.therapistId, override.therapistName, service) }
				: {}),
			...(!keepManualRoomSelection && override.roomId != null ? {
				room: override.roomId,
				roomId: override.roomId,
				room_segments: Array.isArray(service?.room_segments) && service.room_segments.length > 0
					? service.room_segments.map((segment) => ({
						...segment,
						room_id: override.roomId,
					}))
					: service?.room_segments,
			} : {}),
			...(!keepManualRoomSelection && override.roomName ? { room_name: override.roomName, roomName: override.roomName } : {}),
			...(!keepManualTimeSelection && typeof override?.absoluteStartMinutes === "number" && !Number.isNaN(override.absoluteStartMinutes)
				? {
					absoluteMinutes: override.absoluteStartMinutes,
					dayOffset: Math.floor(override.absoluteStartMinutes / 1440),
				}
				: {}),
			...(service?.scheduleLinkBroken === true
				? { linkedToPrevious: false, scheduleLinkParentKey: null, scheduleLinkBroken: true }
				: {
					...(!keepManualTimeSelection && (override?.linkedToPrevious === false || override?.linkedToPrevious === true)
						? { linkedToPrevious: override.linkedToPrevious }
						: {}),
					...(!keepManualTimeSelection && override?.scheduleLinkParentKey != null && override.scheduleLinkParentKey !== ""
						? { scheduleLinkParentKey: String(override.scheduleLinkParentKey) }
						: !keepManualTimeSelection && override?.linkedToPrevious === false
							? { scheduleLinkParentKey: null }
							: {}),
				}),
			...(!keepManualTimeSelection && override?.service_at ? {
				start_date: overrideRange.start_date,
				end_date: overrideRange.end_date,
				dayOffset: overrideTimeline.dayOffset,
				absoluteMinutes: typeof override?.absoluteStartMinutes === "number" && !Number.isNaN(override.absoluteStartMinutes)
					? override.absoluteStartMinutes
					: overrideTimeline.absoluteMinutes,
				is_calendar_expansion: getItemCalendarExpansionFlag(
					{
						...service,
						dayOffset: overrideTimeline.dayOffset,
						absoluteMinutes: typeof override?.absoluteStartMinutes === "number"
							? override.absoluteStartMinutes
							: overrideTimeline.absoluteMinutes,
					},
					overrideRange.start_date
				),
				cross_day_booking_item: overrideCrossDayBookingItem,
			} : {}),
		};
	};

	const getEffectiveBookingDateTime = (booking, currentPrefill) => {
		const bookingItemEntries = Object.entries(booking?.booking_item || {});
		if (bookingItemEntries.length === 0) return null;

		let primaryCustomerKey = null;
		for (const [customerNameKey, items] of bookingItemEntries) {
			const hasPrimaryItem = Array.isArray(items) && items.some((item) => item?.primary === 1 || item?.primary === true);
			if (hasPrimaryItem) {
				primaryCustomerKey = customerNameKey;
				break;
			}
		}

		if (!primaryCustomerKey) {
			primaryCustomerKey = bookingItemEntries[0]?.[0] || null;
		}

		const anchoredItems = Array.isArray(booking?.booking_item?.[primaryCustomerKey])
			? booking.booking_item[primaryCustomerKey]
			: bookingItemEntries[0]?.[1];
		const anchoredItem = Array.isArray(anchoredItems) ? anchoredItems[0] : null;
		if (!anchoredItem) return null;

		const override = getServiceOverride(anchoredItem?.id ?? anchoredItem?.booking_item_id, currentPrefill);
		const resolvedServiceAt = override?.service_at || anchoredItem?.service_at;
		const parsedDate = parseServiceAtDate(resolvedServiceAt);
		const overrideStartDate = parsedDate ? formatDateToDDMMYYYY(parsedDate) : null;
		if (!parsedDate) return null;

		const hasDragTimeOverride = !!(override?.start || override?.service_at);

		return {
			parsedDate,
			item: {
				...anchoredItem,
				...(override?.service_at ? {
					service_at: override.service_at,
					start_date: overrideStartDate,
					// Stale API cross-day segments must not override dragged start time in header init.
					cross_day_booking_item: null,
				} : {}),
				...(override?.start ? { start_time: override.start } : {}),
				...(override?.end ? { end_time: override.end } : {}),
				...(hasDragTimeOverride && !override?.service_at ? { cross_day_booking_item: null } : {}),
			},
		};
	};

	const getServiceDisplayStartDate = useCallback((serviceLike) => {
		const segmentPosition = getSelectedDayCrossDaySegmentPosition(serviceLike, resolvedEditDate);
		const segments = Array.isArray(serviceLike?.cross_day_booking_item)
			? serviceLike.cross_day_booking_item
			: [];
		if (segmentPosition === "first") {
			return normalizeDateString(segments[0]?.start_date) || normalizeDateString(serviceLike?.start_date);
		}
		if (segmentPosition === "second") {
			return normalizeDateString(segments[1]?.start_date) || normalizeDateString(serviceLike?.start_date);
		}
		const parsedServiceAtDate = parseServiceAtDate(serviceLike?.service_at);
		return parsedServiceAtDate
			? formatDateToDDMMYYYY(parsedServiceAtDate)
			: normalizeDateString(serviceLike?.start_date);
	}, [getSelectedDayCrossDaySegmentPosition, normalizeDateString, parseServiceAtDate, resolvedEditDate]);

	const getSelectedDayProjectedServiceRange = useCallback((serviceLike, baseDateInput = resolvedEditDate) => {
		const normalizedBaseDate = normalizeDateString(baseDateInput);
		const fallbackStartDate = normalizeDateString(serviceLike?.start_date);
		const fallbackEndDate = normalizeDateString(serviceLike?.end_date) || fallbackStartDate;
		const fallbackRange = {
			start_date: fallbackStartDate,
			end_date: fallbackEndDate,
			start_time: extractHHMM(serviceLike?.start_time || serviceLike?.service_at?.split(" ")[1] || ""),
			end_time: extractHHMM(serviceLike?.end_time || ""),
			duration: Number(serviceLike?.duration) || 0,
		};

		if (!normalizedBaseDate) {
			return fallbackRange;
		}

		if (!isActualSplitBookingService(serviceLike)) {
			return fallbackRange;
		}

		const segments = Array.isArray(serviceLike?.cross_day_booking_item)
			? serviceLike.cross_day_booking_item
			: [];
		if (segments.length < 2) {
			return fallbackRange;
		}

		const matchingSegment = segments.find(
			(segment) => normalizeDateString(segment?.start_date) === normalizedBaseDate
		);
		if (!matchingSegment) {
			return fallbackRange;
		}

		return {
			start_date: normalizeDateString(matchingSegment?.start_date) || fallbackRange.start_date,
			end_date:
				normalizeDateString(matchingSegment?.end_date) ||
				normalizeDateString(matchingSegment?.start_date) ||
				fallbackRange.end_date,
			start_time: extractHHMM(matchingSegment?.start_time || "") || fallbackRange.start_time,
			end_time: extractHHMM(matchingSegment?.end_time || "") || fallbackRange.end_time,
			duration: Number(matchingSegment?.duration) || fallbackRange.duration,
		};
	}, [extractHHMM, isActualSplitBookingService, normalizeDateString, resolvedEditDate]);


	const getHomeGridOverrideProjectedRange = useCallback((serviceLike, baseDateInput = resolvedEditDate) => {
		const defaultRange = getSelectedDayProjectedServiceRange(serviceLike, baseDateInput);
		if (!baseDateInput || !serviceLike) return defaultRange;

		const selectedYmd = formatDateToYMD(
			baseDateInput instanceof Date ? baseDateInput : new Date(baseDateInput)
		);
		if (!selectedYmd) return defaultRange;

		const segments = Array.isArray(serviceLike?.cross_day_booking_item)
			? serviceLike.cross_day_booking_item
			: [];
		if (segments.length < 2) return defaultRange;

		const firstSeg = segments[0];
		const secondSeg = segments[1];
		const lastSeg = segments[segments.length - 1];

		const firstStart = firstSeg?.start_time || null;
		const firstEnd = firstSeg?.end_time || null;
		const secondStart = secondSeg?.start_time || null;
		const secondEnd = secondSeg?.end_time || null;
		if (!firstStart || !firstEnd || !secondStart || !secondEnd) return defaultRange;

		const firstStartMinutes = convertToMinutes(extractHHMM(firstStart));
		const secondStartMinutes = convertToMinutes(extractHHMM(secondStart));
		if (
			Number.isNaN(firstStartMinutes) ||
			Number.isNaN(secondStartMinutes) ||
			firstStartMinutes >= 6 * 60 ||
			secondStartMinutes !== 6 * 60
		) {
			return defaultRange;
		}

		const allSameTimes = segments.every((segment) =>
			(segment?.start_time || null) === firstStart &&
			(segment?.end_time || null) === firstEnd
		);
		if (allSameTimes) return defaultRange;

		const segmentDateToYmd = (dateStr) => {
			if (!dateStr || typeof dateStr !== "string") return null;
			const [day, month, year] = dateStr.split("-");
			if (!day || !month || !year) return null;
			return `${year}-${month}-${day}`;
		};
		if (segmentDateToYmd(secondSeg?.start_date) !== selectedYmd) {
			return defaultRange;
		}

		const totalDuration = segments.reduce((sum, s) => sum + (Number(s?.duration) || 0), 0)
			|| Number(serviceLike?.duration) || 0;

		return {
			start_date: normalizeDateString(secondSeg?.start_date) || defaultRange.start_date,
			end_date:
				normalizeDateString(lastSeg?.end_date || lastSeg?.start_date) ||
				defaultRange.end_date,
			start_time: extractHHMM(firstSeg?.start_time || "") || defaultRange.start_time,
			end_time: extractHHMM(lastSeg?.end_time || "") || defaultRange.end_time,
			duration: totalDuration || defaultRange.duration,
		};
	}, [formatDateToYMD, getSelectedDayProjectedServiceRange, normalizeDateString, resolvedEditDate]);

	const editBookingHeaderInitKeyRef = useRef(null);

	useEffect(() => {
		if (!editableBooking?.booking_item) return;
		// First-service drag prefill syncs header from selectedServices[0]; other items keep At: on service 1.
		if (prefill?.startTime && isPrefillForFirstMainService(prefill)) return;

		const initKey = `${editableBooking.id}:${normalizeDateString(selectedDate)}`;
		if (editBookingHeaderInitKeyRef.current === initKey) return;

		// User already changed date/time/services — DateTimeOutletSelector + sync own the header.
		if (bookingChanged) {
			editBookingHeaderInitKeyRef.current = initKey;
			return;
		}

		const effectiveStartMeta = getEffectiveBookingDateTime(editableBooking, prefill);
		const effectiveDate = effectiveStartMeta?.parsedDate;
		const effectiveItem = effectiveStartMeta?.item;
		if (!effectiveDate) return;

		const viewedDateStr = normalizeDateString(selectedDate);
		const crossDaySegments = Array.isArray(effectiveItem?.cross_day_booking_item)
			? effectiveItem.cross_day_booking_item
			: [];
		const viewedDateSegment = viewedDateStr
			? crossDaySegments.find(
				(segment) => normalizeDateString(segment?.start_date) === viewedDateStr
			)
			: null;
		const valueTime = extractHHMM(
			viewedDateSegment?.start_time ||
			effectiveItem?.start_time ||
			(typeof effectiveItem?.service_at === "string" ? effectiveItem.service_at.split(" ")[1] : "") ||
			effectiveDate.toTimeString().split(" ")[0].slice(0, 5)
		);
		const labelTime = convertTo12Hour(valueTime);
		const effectiveStartDate =
			viewedDateSegment?.start_date ??
			effectiveItem?.start_date ??
			formatDateToDDMMYYYY(effectiveDate);
		const effectiveDayOffset = viewedDateSegment
			? 0
			: getResolvedServiceDayOffset(
				effectiveItem,
				effectiveStartDate,
				valueTime
			);
		const normalizedTime = buildTimeSelection(
			valueTime,
			effectiveDayOffset,
			labelTime,
			(effectiveDayOffset * 1440) + convertToMinutes(valueTime)
		);
		const initialEditDate =
			selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())
				? new Date(selectedDate)
				: (effectiveDayOffset > 0 && selectedDate ? new Date(selectedDate) : effectiveDate);

		editBookingHeaderInitKeyRef.current = initKey;

		setEditBooking({
			date: initialEditDate,
			time: normalizedTime,
			outlet: editableBooking.outlet_id,
		});
		setSelectedTime(normalizedTime);
		setBookingData((prev) => ({
			...prev,
			outlet: editableBooking.outlet_id,
		}));
	}, [
		editableBooking,
		prefill?.startTime,
		prefill?.itemId,
		isPrefillForFirstMainService,
		setBookingData,
		setSelectedTime,
		extractHHMM,
		getResolvedServiceDayOffset,
		normalizeDateString,
		selectedDate,
		bookingChanged,
	]);

	// Ensure prefill applies to bottom services once services are loaded (first-open race fix)
	const prefillAppliedRef = useRef(false);
	const lastPrefillSigRef = useRef(null);

	useEffect(() => {
		if (!prefill) return;
		const currentOverrides = getPrefillOverrides(prefill);
		const currentSig = JSON.stringify({
			bookingId: prefill.bookingId || "",
			itemId: prefill.itemId || "",
			bookingOverrides: currentOverrides,
		});
		if (lastPrefillSigRef.current !== currentSig) {
			lastPrefillSigRef.current = currentSig;
			prefillAppliedRef.current = false;
		}
		if (prefillAppliedRef.current) {
			const allLoadedServices = [
				...(Array.isArray(selectedServices) ? selectedServices : []),
				...(Array.isArray(paxes) ? paxes.flatMap((pax) => pax.selectedServices || []) : []),
			];
			const needsRoomReapply = allLoadedServices.some((svc) => {
				// Dropdown after drag intentionally differs from prefill — do not reapply/loop.
				if (svc?.hasManualRoomSelection === true) return false;
				const override = getServiceOverride(svc?.booking_item_id, prefill);
				if (!override || override.roomId == null) return false;
				const currentRoomId = svc?.roomId ?? (typeof svc?.room === "object" ? svc.room?.value : svc?.room);
				return Number(currentRoomId) !== Number(override.roomId);
			});
			if (!needsRoomReapply) return;
			prefillAppliedRef.current = false;
		}
		const overrideIds = Object.keys(currentOverrides || {});
		if (overrideIds.length === 0) return;
		const matchExists = (
			(Array.isArray(selectedServices) && selectedServices.some((svc) => overrideIds.includes(String(svc?.booking_item_id)))) ||
			(Array.isArray(paxes) && paxes.some((pax) => Array.isArray(pax?.selectedServices) && pax.selectedServices.some((svc) => overrideIds.includes(String(svc?.booking_item_id)))))
		);
		if (!matchExists) return; // wait until services with matching item appear


		try {
			setSelectedServices(prev => (
				Array.isArray(prev)
					? prev.map((svc) => applyOverrideToService(svc, prefill))
					: prev
			));

			setPaxes(prev => (
				Array.isArray(prev)
					? prev.map(pax => ({
						...pax,
						selectedServices: Array.isArray(pax.selectedServices)
							? pax.selectedServices.map((svc) => applyOverrideToService(svc, prefill))
							: pax.selectedServices
					}))
					: prev
			));
			setSelectedRooms((prevRooms) => {
				const next = { ...(prevRooms || {}) };
				const allServices = [
					...(Array.isArray(selectedServices) ? selectedServices : []),
					...(Array.isArray(paxes) ? paxes.flatMap((pax) => pax.selectedServices || []) : []),
				];
				allServices.forEach((svc) => {
					if (svc?.hasManualRoomSelection === true) return;
					const override = getServiceOverride(svc?.booking_item_id, prefill);
					if (!override || override.roomId == null || svc?.counterKey == null) return;
					next[svc.counterKey] = {
						...(next[svc.counterKey] || {}),
						value: override.roomId,
						item_id: override.roomId,
						label: override.roomName || next[svc.counterKey]?.label || "Room Selected",
						item_name: override.roomName || next[svc.counterKey]?.item_name || "Room Selected",
					};
				});
				return next;
			});
			prefillAppliedRef.current = true;
			setBookingChanged(true);
		} catch {}
	}, [prefill, selectedServices, paxes, setSelectedServices, setPaxes, setSelectedRooms, setBookingChanged]);

	// Home blocked a stale hydrate on the grid — re-apply drag prefill so Save uses the drop values.
	useEffect(() => {
		const onReapply = (e) => {
			if (!prefill) return;
			const detailBookingId = e?.detail?.bookingId;
			if (
				detailBookingId != null && prefill?.bookingId != null && String(detailBookingId) !== String(prefill.bookingId)
			) { return; }
			try {
				setSelectedServices((prev) => {
					if (!Array.isArray(prev)) return prev;
					return prev.map((svc) => applyOverrideToService(svc, prefill));
				});
				setPaxes((prev) => Array.isArray(prev) ? prev.map((pax) => ({ ...pax, selectedServices: Array.isArray(pax.selectedServices) ? pax.selectedServices.map((svc) => applyOverrideToService(svc, prefill)) : pax.selectedServices,  })): prev);
				setBookingChanged(true);
			} catch {}
		};
		window.addEventListener("reapplyCalendarDragPrefill", onReapply);
		return () => window.removeEventListener("reapplyCalendarDragPrefill", onReapply);
	}, [prefill, setSelectedServices, setPaxes, setBookingChanged]);

	// Header / service-time picks land in selectedServices first. Copy those times onto
	// editPrefill so a later Save does not rebuild the payload from the drag snapshot.
	useEffect(() => {
		const updated = manualTimeSyncRef.current;
		if (!Array.isArray(updated)) return;
		manualTimeSyncRef.current = null;
		syncEditPrefillFromManualTimes(updated);
	}, [selectedServices, syncEditPrefillFromManualTimes]);

	useEffect(() => {
		const onManualTimeSync = (event) => {
			const services = event?.detail?.services;
			if (!Array.isArray(services)) return;
			syncEditPrefillFromManualTimes(services);
		};
		window.addEventListener("editManualTimeSync", onManualTimeSync);
		return () => window.removeEventListener("editManualTimeSync", onManualTimeSync);
	}, [syncEditPrefillFromManualTimes]);

	useEffect(() => {
		if (!editableBooking) return;
		if (editableBooking?.id && hydratedBookingItemsRef.current === String(editableBooking.id)) {
			return;
		}

		const bookingItems = editableBooking?.booking_item || {};
		const bookingItemEntries = Object.entries(bookingItems);
		const paxesData = [];
		let paxIdCounter = 1;

		// Find the primary customer by checking for items with primary === 1
		// This fixes the issue where numeric guest names cause incorrect ordering
		let primaryCustomerKey = null;
		for (const [customerNameKey, items] of bookingItemEntries) {
			const hasPrimaryItem = Array.isArray(items) && items.some(item => item.primary === 1 || item.primary === true);
			if (hasPrimaryItem) {
				primaryCustomerKey = customerNameKey;
				break;
			}
		}
		// Fallback to first entry if no primary customer found
		if (!primaryCustomerKey && bookingItemEntries.length > 0) {
			primaryCustomerKey = bookingItemEntries[0][0];
		}

		//Treat primary customer as main customer, or first entry if no primary found
		bookingItemEntries.forEach(([customerNameKey, items], index) => {
			const isSelf = customerNameKey === primaryCustomerKey;
			const currentPaxId = `P${paxIdCounter}`;

			const mappedServices = items.map((svc, i) => {
				const therapistObj = homeStaffList?.find((staff) => staff.id === svc.therapist_id);
				const therapistId = therapistObj?.id ?? svc.therapist_id ?? null;
				const therapistName = therapistObj?.name ?? svc.therapist ?? "";
				const counterKey = isSelf ? `${i + 1}` : `${currentPaxId}-${i + 1}`;
				const roomItem = Array.isArray(svc.room_items) && svc.room_items.length > 0 ? svc.room_items[0] : null;
				const room_name = roomItem?.room_name || "";
				const room_id = roomItem?.room_id || null;
				const parsedStartTime = svc.service_at?.split(" ")[1]?.slice(0, 5) || "";
				const parsedServiceAtDate = parseServiceAtDate(svc.service_at);
				const parsedStartDate = getServiceDisplayStartDate(svc) ?? (parsedServiceAtDate ? formatDateToDDMMYYYY(parsedServiceAtDate) : null);
				const resolvedDayOffset = getResolvedServiceDayOffset(svc, parsedStartDate, parsedStartTime);
				const resolvedBaseDate = getEditBaseDateStr() || normalizeDateString(selectedDate) || parsedStartDate;
				const resolvedStartDate =
					resolvedDayOffset !== 0 && resolvedBaseDate
						? addDaysToDateString(resolvedBaseDate, resolvedDayOffset)
						: parsedStartDate;
				const resolvedDateRange = buildDateRangeFromResolvedStartDate(
					resolvedStartDate,
					parsedStartTime,
					svc.end_time || ""
				);
				const parsedTimelineMeta = buildTimelineMetaForService(
					resolvedDateRange.start_date,
					parsedStartTime,
					selectedDate
				);
				const lockedPricing = getLockedBookingItemPricing(svc);
				const savedMembershipApplied = lockedPricing
					? lockedPricing.isMembershipApplied
					: svc?.isMembershipApplied;
				const savedMemberRateApplied = lockedPricing
					? lockedPricing.isMemberRateApplied
					: svc?.isMemberRateApplied;

				// Create selected_therapist object if therapist exists
				const selectedTherapist = (therapistId != null) ? {
					value: Number(therapistId),
					label: therapistName,
					pager: therapistObj?.pager_number || svc.therapist_pager_number || "",
					gender: therapistObj?.gender || svc.gender || "",
				} : null;

				// Normalize addons from API to [{id, name, quantity}, ...] (same as ServiceSelector)
				const normalizedAddons = (() => {
					const raw = svc.addons;
					if (!Array.isArray(raw) || raw.length === 0) return [];
					return raw
						.map((a) => ({
							id: Number(a.id ?? a.addon_id ?? a.item_id ?? 0),
							name: String(a.name ?? a.addon_name ?? a.item_name ?? ""),
							quantity: Number(a.quantity ?? 1) || 1,
							unit_price: a?.unit_price,
							booking_item_addon_id: a?.booking_item_addon_id,
						}))
						.filter((a) => a.id && a.name);
				})();

				// Base parsed service from API
				const parsed = {
					service: Number(svc.service_id),
					booking_item_id: svc.id ?? svc.booking_item_id ?? null,
					name: svc.service_name || svc.name || svc.service,
					service_at: typeof svc.service_at === "string" ? svc.service_at : null,
					duration: Number(svc.duration) || 0,
					start_time: svc.service_at?.split(" ")[1] || "",
					end_time: svc.end_time || "",
					room: svc.room_id || room_id,
					room_name: svc.room_name ||room_name,
					therapist: therapistId,
					therapist_name: therapistName,
					selected_therapist: selectedTherapist, // ✅ Set selected_therapist when loading from editableBooking
					service_request: svc.service_request || "",
					requested_person: svc.requested_person === 1 ? 1 : 0,
					requested_therapist: svc.requested_person === 1,
					requested_room: ((roomItem?.requested_room ?? svc.requested_room) === 1) ? 1 : 0,
					requested_therapist_disabled: false,
					counterKey,
					price: svc.price,
					cross_day_booking_item: svc.cross_day_booking_item || null,
					start_date: resolvedDateRange.start_date ?? null,
					end_date: resolvedDateRange.end_date ?? null,
					dayOffset: parsedTimelineMeta.dayOffset,
					absoluteMinutes: parsedTimelineMeta.absoluteMinutes,
					is_calendar_expansion: svc?.is_calendar_expansion,
					isMemberRateApplied: savedMemberRateApplied,
					isMembershipApplied: savedMembershipApplied,
					addons: normalizedAddons,
					price_rate: svc.price_rate || null,
				};

				return applyOverrideToService(parsed, prefill);
			});

			const linkedMappedServices = ensureServiceLinkedFlags(
				markMismatchedEditChainLinksBroken(mappedServices)
			);
			// Cross-day chain: linked early-morning children after a midnight-crossing
			// parent must keep the parent's end_date (e.g. 00:45 on Apr 11).
			// Evening children (e.g. 11:40 PM) stay on the parent's start day — rewriting
			// them to end_date made the first edit time pick snap to +1 incorrectly.
			const chainCorrectedServices = linkedMappedServices.map((svc, serviceIdx) => {
				if (serviceIdx === 0 || svc?.linkedToPrevious === false) return svc;
				const previousService = linkedMappedServices[serviceIdx - 1];
				const prevStart = normalizeDateString(previousService?.start_date);
				const prevEnd = normalizeDateString(previousService?.end_date);
				const prevEndMinutes = convertToMinutes(extractHHMM(previousService?.end_time || ""));
				const parentCrossesMidnight = !!(prevStart && prevEnd && prevStart !== prevEnd);
				const parentEndsEarlyMorning = !Number.isNaN(prevEndMinutes)
					&& prevEndMinutes >= 0
					&& prevEndMinutes < extendedBusinessCutoffMinutes;
				if (!prevEnd || (!parentCrossesMidnight && !parentEndsEarlyMorning)) {
					return svc;
				}
				const normalizedStartTime = extractHHMM(svc.start_time || "");
				const childStartMinutes = convertToMinutes(normalizedStartTime);
				// Same rule as handleTimeClick: only morning clocks belong on parent end_date.
				const childIsEarlyMorning = !Number.isNaN(childStartMinutes) && childStartMinutes >= 0 && childStartMinutes < extendedBusinessCutoffMinutes;
				if (!childIsEarlyMorning) {
					return svc;
				}
				const childStart = normalizeDateString(svc.start_date);
				if (childStart === prevEnd) return svc;

				const chainDateRange = buildDateRangeFromResolvedStartDate(
					prevEnd,
					normalizedStartTime,
					svc.end_time || ""
				);
				const chainTimelineMeta = buildTimelineMetaForService(
					chainDateRange.start_date,
					normalizedStartTime,
					selectedDate
				);
				return {
					...svc,
					start_date: chainDateRange.start_date,
					end_date: chainDateRange.end_date,
					dayOffset: chainTimelineMeta.dayOffset,
					absoluteMinutes: chainTimelineMeta.absoluteMinutes,
				};
			});
			if (isSelf) {
				setSelectedServices(chainCorrectedServices);
			} else {
				paxesData.push({
					id: currentPaxId,
					customer_name: customerNameKey,
					selectedServices: chainCorrectedServices,
					showNameInput: true,
					isSelf: false,
				});
				paxIdCounter++;
			}
		});

		setPaxes(paxesData);
		setBookingType(paxesData.length > 0 ? 2 : 1);

		setItemData((prev) => ({
			...(prev || {}),
			company: 1,
			panel: "outlet",
			newsletter: 1,
			currency: "SGD",
			payment_type: "payatstore",
			payment_status: 1,
			type:"manual",
			customer: editableBooking?.user?.[0]?.id,
			booking_type: paxesData.length > 0 ? 2 : 1,
		}));

		// Use first service to set booking time
		const firstService = bookingItemEntries?.[0]?.[1]?.[0];
		const firstTime = firstService?.service_at
			?.split(" ")[1]
			?.slice(0, 5);

		if (firstTime) {
			const firstServiceAtDate = parseServiceAtDate(firstService?.service_at);
		const firstServiceDate = getServiceDisplayStartDate(firstService) ?? (firstServiceAtDate ? formatDateToDDMMYYYY(firstServiceAtDate) : null);
			const firstTimelineMeta = buildTimelineMetaForService(firstServiceDate, firstTime);
			setBookingData((prev) => ({
				...prev,
				time: buildTimeSelection(
					firstTime,
					firstTimelineMeta.dayOffset,
					convertTo12Hour(firstTime),
					firstTimelineMeta.absoluteMinutes
				),
			}));
		}
		if (editableBooking?.id) {
			hydratedBookingItemsRef.current = String(editableBooking.id);
		}
	}, [editableBooking, homeStaffList, buildTimelineMetaForService, getResolvedServiceDayOffset, getServiceDisplayStartDate, addDaysToDateString, selectedDate, resolveSavedBooleanFlag, isMembershipApplied, getLockedBookingItemPricing]);

	// Enrich services with member_price from service catalog for accurate pricing on membership toggle
	useEffect(() => {
		const enrichPrices = async () => {
			try {
				const collectIds = () => {
					const ids = new Set();
					(Array.isArray(selectedServices) ? selectedServices : []).forEach((s) => { if (s?.service || s?.id) ids.add(Number(s.service || s.id)); });
					(Array.isArray(paxes) ? paxes : []).forEach((p) => {
						(Array.isArray(p?.selectedServices) ? p.selectedServices : []).forEach((s) => { if (s?.service || s?.id) ids.add(Number(s.service || s.id)); });
					});
					return Array.from(ids);
				};

				const serviceIds = collectIds();
				if (serviceIds.length === 0) return;

				let catalog = [];
				try {
					const ss = sessionStorage.getItem('serviceList');
					catalog = ss ? JSON.parse(ss) : [];
				} catch {}

				if (!Array.isArray(catalog) || catalog.length === 0) {
					try {
						const resp = await getServicesList({
							pagination: 0,
							page: 1,
							outlet: selectedOutlet,
							status: 1,
							outlet_type: selectedOutletData?.outlet_type === 'Classic' ? 2 : 1,
						});
						catalog = resp?.data?.data?.list?.category || [];
					} catch {}
				}

				const map = new Map();
				catalog.forEach((cat) => {
					(cat?.services || []).forEach((svc) => {
						if (svc?.id != null) {
							map.set(Number(svc.id), {
								price: svc.price,
								member_price: svc.member_price,
								name: svc.name,
							});
						}
					});
				});

				const applyMap = (svc) => {
					const idNum = Number(svc.service || svc.id);
					const row = map.get(idNum);
					if (!row) return svc;
					return {
						...svc,
						price: svc.price ?? row.price,
						member_price: svc.member_price ?? row.member_price,
						name: svc.name || row.name,
					};
				};

				// update main selected services
				if (Array.isArray(selectedServices) && selectedServices.length > 0) {
					setSelectedServices((prev) => prev.map(applyMap));
				}
				// update pax services
				if (Array.isArray(paxes) && paxes.length > 0) {
					setPaxes((prev) => prev.map((p) => ({
						...p,
						selectedServices: Array.isArray(p.selectedServices) ? p.selectedServices.map(applyMap) : p.selectedServices,
					})));
				}

				// no-op
			} catch {}
		};

		enrichPrices();
	}, [editableBooking, isMembershipApplied, selectedOutlet, selectedOutletData?.outlet_type]);

	useEffect(() => {
		if (!selectedServices || selectedServices.length === 0) return;

		const mainCustomerName =
			customerData?.firstname && customerData?.lastname
				? `${customerData.firstname} ${customerData.lastname}`.trim()
				: editableBooking?.user?.[0]?.name || customerData.name;

		// Get guest check-in tag for primary customer
		const customerGuestCheckin = getGuestCheckinPayload('customer');

		const allServices = selectedServices.map((svc) => ({
			...svc,
			customer_name: svc.customer_name || mainCustomerName, //ensure name set for self
		}));

		paxes?.forEach((pax) => {
			pax.selectedServices?.forEach((svc) => {
				allServices.push({
					...svc,
					customer_name: pax.customer_name,
				});
			});
		});

		
		const resolvePrice = (svc) => {

			const lockedPricing = getLockedBookingItemPricing(svc);
			if (lockedPricing && lockedPricing.price !== undefined && lockedPricing.price !== null) {
				return lockedPricing.price;
			}
			const baseKey = svc.splitParent || svc.counterKey;
			const baseSvc = (selectedServices || []).find((s) => !s.isSplitSegment && String(s.counterKey) === String(baseKey));
			const candidate = isMembershipApplied ? (svc.member_price ?? baseSvc?.member_price) : (svc.price ?? baseSvc?.price);

			const finalPrice = isMembershipApplied ? candidate : (svc.price ?? baseSvc?.price);
			const val = isPremiumOutlet ? finalPrice : (candidate == null || candidate === "") ? 0 : candidate;
			return String(val);
		};

		const itemsArray = allServices.map((svc) => {
			const baseKey = svc.splitParent || svc.counterKey;
			const baseSvc = (selectedServices || []).find((s) => !s.isSplitSegment && String(s.counterKey) === String(baseKey));
			const isSplit = !!svc.isSplitSegment;
			// New services should send id: null; existing services keep their own id. Splits omit id.
			const idVal = isSplit
				? undefined
				: (svc.booking_item_id != null ? svc.booking_item_id : null);
			
			// Determine if this service belongs to a pax or the customer
			const isPaxService = paxes?.some(pax => 
				pax.selectedServices?.some(ps => ps.counterKey === svc.counterKey)
			);
			
			// Get guest check-in ID based on whether it's a customer or pax service
			let guestCheckinId = null;
			let guestCheckinName = null;
			
			if (isPaxService) {
				// Find which pax this service belongs to
				const matchingPax = paxes?.find(pax => 
					pax.selectedServices?.some(ps => ps.counterKey === svc.counterKey)
				);
				if (matchingPax) {
					const paxGuestCheckin = getGuestCheckinPayload(matchingPax.id);
					if (paxGuestCheckin.hasSelection) {
						guestCheckinId = paxGuestCheckin.guestCheckinId;
						guestCheckinName = paxGuestCheckin.guestCheckinName;
					} else {
						guestCheckinId = svc.guest_checkin_id ?? baseSvc?.guest_checkin_id ?? null;
						guestCheckinName = svc.guest_checkin_name ?? baseSvc?.guest_checkin_name ?? null;
					}
				}
			} else {
				// Customer service
				guestCheckinId = customerGuestCheckin.hasSelection ? customerGuestCheckin.guestCheckinId : (svc.guest_checkin_id ?? baseSvc?.guest_checkin_id ?? null);
				guestCheckinName = customerGuestCheckin.hasSelection ? customerGuestCheckin.guestCheckinName : (svc.guest_checkin_name ?? baseSvc?.guest_checkin_name ?? null);
			}
			const below12 = isPaxService
				? (() => {
					const matchingPax = paxes?.find(pax =>
						pax.selectedServices?.some(ps => ps.counterKey === svc.counterKey)
					);
					const paxGuestCheckin = matchingPax ? getGuestCheckinPayload(matchingPax.id) : { hasSelection: false };
					return paxGuestCheckin.hasSelection ? paxGuestCheckin.below12 : Number(svc.below_12 ?? baseSvc?.below_12 ?? 0);
				})()
				: (customerGuestCheckin.hasSelection ? customerGuestCheckin.below12 : Number(svc.below_12 ?? baseSvc?.below_12 ?? 0));
			const below12Payload = below12 === 1 ? { below_12: 1 } : {};
			const resolvedGuestCheckinId = below12 === 1 ? null : guestCheckinId;
			const resolvedGuestCheckinName = below12 === 1 ? null : guestCheckinName;
			
			const baseObj = {
				service: svc.service || svc.id,
				customer_name: svc.customer_name,
				start_time: extractHHMM(svc.start_time),
				end_time: extractHHMM(svc.end_time),
				duration: Number(svc.duration),
				therapist: svc.therapist ?? null,
				room: (svc.room ?? baseSvc?.room ?? null),
				requested_person: svc.requested_therapist ? 1 : 0,
				requested_room: svc.requested_room ? 1 : 0,
				price: resolvePrice(svc),
				quantity: "1",
				service_request: svc.service_request || "",
				commission: { tier: null, title: null, rate: "0.00" },
				cross_day_booking_item: svc.cross_day_booking_item ?? baseSvc?.cross_day_booking_item ?? null,
				start_date: svc.start_date ?? baseSvc?.start_date ?? null,
				end_date: svc.end_date ?? baseSvc?.end_date ?? null,
				guest_checkin_id: resolvedGuestCheckinId,
				guest_checkin_name: resolvedGuestCheckinName,
				...below12Payload,
				isMemberRateApplied: svc?.isMemberRateApplied,
				isMembershipApplied: svc?.isMembershipApplied,
			};
			return idVal !== undefined ? { id: idVal, ...baseObj } : baseObj;
		});
		setEditedItemData((prev) => ({
			...prev,
			items: JSON.stringify(itemsArray),
		}));
	}, [selectedServices, paxes, selectedGuestCheckin, customerData, editableBooking, isMembershipApplied, getGuestCheckinPayload, getLockedBookingItemPricing]);

	useEffect(() => {
		if (!editableBooking?.id) return;
		// Wait until services are hydrated so overrides use segment-aware ranges
		// for the viewed day instead of stale parent service_at values.
		if (hydratedBookingItemsRef.current !== String(editableBooking.id)) return;

		const viewedDateStr = normalizeDateString(resolvedEditDate);
		const allServices = [
			...(Array.isArray(selectedServices) ? selectedServices : []),
			...(Array.isArray(paxes)
				? paxes.flatMap((pax) => (Array.isArray(pax?.selectedServices) ? pax.selectedServices : []))
				: []),
		];

		allServices.forEach((service) => {
			const itemId = service?.booking_item_id || service?.item_id;
			const projectedRange = getHomeGridOverrideProjectedRange(service, resolvedEditDate);
			const startTime = projectedRange?.start_time;
			if (!itemId || !startTime) return;

			const projectedDateStr = normalizeDateString(projectedRange?.start_date);
			if (viewedDateStr && projectedDateStr && viewedDateStr !== projectedDateStr) {
				return;
			}

			const serviceStartTime = extractHHMM(service?.start_time || "");
			const isProjectedSegmentSameAsServiceStart = serviceStartTime === startTime;
			const serviceAbs =
				typeof service?.absoluteMinutes === "number" && !Number.isNaN(service.absoluteMinutes)
					? service.absoluteMinutes
					: null;
			const hasMultiDayCrossDay =
				Array.isArray(service?.cross_day_booking_item) &&
				service.cross_day_booking_item.length > 1;
			// Day-2 grid projection of an overnight split (e.g. 23:15 → 00:00 segment) must NOT
			// become a full edit override. That collapses the true previous-day start onto the
			// viewed day whenever a sibling is dragged and selectedServices re-emits.
			if (!isProjectedSegmentSameAsServiceStart && (serviceAbs < 0 || hasMultiDayCrossDay)) {
				return;
			}

			const projectedDayOffset = getResolvedServiceDayOffset(
				service,
				projectedRange?.start_date || resolvedEditDate,
				startTime
			);
			const timelineMeta = buildTimelineMetaForService(
				projectedRange?.start_date || resolvedEditDate,
				startTime,
				resolvedEditDate,
				projectedDayOffset
			);
			// The service's own cached absoluteMinutes/dayOffset reflect its overall
			// start_time. For a genuine split booking, the segment being projected for
			// the currently viewed day (e.g. the 00:00-00:30 tail of a 23:30-00:30 split)
			// can start at a different time than the service's own start_time. In that
			// case the cached value is stale and must not be reused, otherwise the card
			// gets positioned at the wrong row (and can appear to vanish from the grid).
			const resolvedAbsoluteStartMinutes = isProjectedSegmentSameAsServiceStart &&
				typeof service?.absoluteMinutes === "number" &&
				!Number.isNaN(service.absoluteMinutes)
				? service.absoluteMinutes
				: timelineMeta?.absoluteMinutes;

			const roomSelection = service?.counterKey != null ? selectedRooms?.[service.counterKey] : null;
			const serviceTherapistId = service?.therapist ?? service?.selected_therapist?.value ?? null;

			emitProjectedEditOverride({
				bookingId: editableBooking.id,
				itemId,
				start: startTime,
				end: projectedRange?.end_time || service?.end_time,
				therapistId: serviceTherapistId,
				therapistName: service?.therapist_name || service?.selected_therapist?.label || "",
				therapistCleared: !isRoomFilterActive && serviceTherapistId == null,
				roomId: service?.roomId ?? roomSelection?.value ?? service?.room?.value ?? service?.room ?? null,
				roomName: service?.roomName || service?.room_name || roomSelection?.label || "",
				serviceName: service?.name || service?.service_name || "",
				service_category: service?.service_category_name || service?.service_category || "",
				service_at: projectedRange?.start_date ? `${projectedRange.start_date} ${startTime}` : undefined,
				absoluteStartMinutes: resolvedAbsoluteStartMinutes,
				duration: projectedRange?.duration || service?.duration,
				price_rate: service?.price_rate || null,
				linkedToPrevious: service?.linkedToPrevious,
				scheduleLinkParentKey: service?.scheduleLinkParentKey ?? null,
				extra: {counterKey: service?.counterKey ?? null,isPax: String(service?.counterKey || "").includes("-"),},
			});
		});
	}, [editableBooking?.id, selectedServices, paxes, selectedRooms, resolvedEditDate, buildTimelineMetaForService, calendarDateSyncSignal?.refreshKey, emitProjectedEditOverride, getHomeGridOverrideProjectedRange, getResolvedServiceDayOffset, normalizeDateString, isRoomFilterActive]);

	// Ask Home grid logic to recompute grey-box availability when edit-sidebar selections change.
	useEffect(() => {
		const normalizeId = (value) => {
			if (value == null) return null;
			if (typeof value === 'object') {
				const v = value?.value ?? value?.id ?? null;
				return v == null || Number.isNaN(Number(v)) ? null : Number(v);
			}
			return Number.isNaN(Number(value)) ? null : Number(value);
		};
		const allServices = [
			...(Array.isArray(selectedServices) ? selectedServices : []),
			...(Array.isArray(paxes) ? paxes.flatMap((pax) => (Array.isArray(pax?.selectedServices) ? pax.selectedServices : [])) : []),
		];
		const mainService = allServices.find((svc) => !svc?.isSplitSegment) || allServices[0] || null;
		const projectedMainServiceRange = mainService
			? getHomeGridOverrideProjectedRange(mainService, resolvedEditDate)
			: null;
		const counterKey = mainService?.counterKey;
		const roomItemId = counterKey != null ? normalizeId(selectedRooms?.[counterKey]?.value): null;
		const mainServiceTimeline = projectedMainServiceRange?.start_time
			? buildTimelineMetaForService(
				projectedMainServiceRange?.start_date || resolvedEditDate,
				projectedMainServiceRange.start_time,
				resolvedEditDate
			)
			: null;
		const detail = {
			mode: isRoomFilterActive ? 'room' : 'therapist',
			time: projectedMainServiceRange?.start_time || editBooking?.time?.value || null,
			absoluteStartMinutes: mainServiceTimeline?.absoluteMinutes,
			therapistId: normalizeId(mainService?.therapist ?? mainService?.selected_therapist),
			roomItemId,
			serviceTargets: allServices.map((service) => {
				const projectedRange = getHomeGridOverrideProjectedRange(service, resolvedEditDate);
				const serviceTime = projectedRange?.start_time || editBooking?.time?.value || null;
				const timelineMeta = serviceTime
					? buildTimelineMetaForService(
						projectedRange?.start_date || resolvedEditDate,
						serviceTime,
						resolvedEditDate
					)
					: null;
				return {
					counterKey: service?.counterKey ?? null,
					time: serviceTime,
					absoluteStartMinutes: timelineMeta?.absoluteMinutes,
					therapistId: normalizeId(service?.therapist ?? service?.selected_therapist),
					roomItemId: service?.counterKey != null
						? normalizeId(selectedRooms?.[service.counterKey]?.value) ?? normalizeId(service?.roomId ?? service?.room?.value)
						: normalizeId(service?.roomId ?? service?.room?.value),
				};
			}).filter((target) => target?.time && (target?.therapistId != null || target?.roomItemId != null)),
		};
		try {
			window.dispatchEvent(new CustomEvent('editSidebarGreyBoxRecheck', { detail }));
		} catch {}
	}, [selectedServices, paxes, selectedRooms, editBooking?.time?.value, isRoomFilterActive, buildTimelineMetaForService, resolvedEditDate, getHomeGridOverrideProjectedRange]);

	// Sync source from editable booking
	useEffect(() => {
		if (editableBooking?.source) {
			setEditedItemData((prev) => ({
				...prev,
				source: editableBooking.source,
				note: editableBooking.note,
				outlet: editableBooking.outlet_id || selectedOutlet,
				status: editableBooking.status,
			}));
		}
	}, [editableBooking]);

	useEffect(() => {
		if (editableBooking?.user) {
			setEditedItemData((prev) => ({
				...prev,
				user: editableBooking.user,
			}));
		}
	}, [editableBooking]);
	
	// Validation function for edit booking
	const validateEditFields = () => {
		let isValid = true;

		if (!editableBooking.user) {
			setCustomerError("Please select a customer.");
			isValid = false;
		}

		if (!selectedServices || selectedServices.length === 0) {
			setServiceError("Please select at least one service.");
			isValid = false;
		}

		if (!editedItemData.source) {
			setServiceError("Please select a source.");
			isValid = false;
		}

		return isValid;
	};

	// Compute isCommissionUpdate: true when customer, membership, service, therapist, or start time changed
	const isCommissionUpdate = (() => {
		if (!editableBooking) return false;
		// Resolve original/current customer - support both array and object structure
		const origUser = editableBooking?.user;
		const originalCustomerId = (Array.isArray(origUser) && origUser[0] != null)
			? (origUser[0].id != null ? Number(origUser[0].id) : null)
			: (origUser?.id != null ? Number(origUser.id) : null);
		const currUser = customerData;
		const currentCustomerId = (Array.isArray(currUser) && currUser[0] != null)
			? (currUser[0].id != null ? Number(currUser[0].id) : null)
			: (currUser?.id != null ? Number(currUser.id) : null);
		if (originalCustomerId !== currentCustomerId) return true;
		// Original membership: prefer booking_item item, fallback to user.membership; only check if we can determine original
		let originalMembership = null;
		const bookingItems = editableBooking?.booking_item || {};
		for (const arr of Object.values(bookingItems)) {
			if (Array.isArray(arr) && arr.length > 0 && arr[0] != null) {
				const itemVal = arr[0].isMembershipApplied;
				if (itemVal !== undefined && itemVal !== null) {
					originalMembership = itemVal === true || itemVal === 1;
					break;
				}
			}
		}
		if (originalMembership === null) {
			const userMembership = (Array.isArray(origUser) && origUser[0] != null)
				? origUser[0].membership
				: origUser?.membership;
			if (userMembership !== undefined && userMembership !== null) {
				originalMembership = Number(userMembership) === 1;
			}
		}
		if (originalMembership !== null && originalMembership !== isMembershipApplied) return true;
		// Build map of original items: booking_item_id -> { service_id, therapist_id, start_time }
		const originalMap = new Map();
		for (const arr of Object.values(bookingItems)) {
			if (!Array.isArray(arr)) continue;
			for (const item of arr) {
				if (item.id == null) continue;
				const rawTime = (item.service_at?.split?.(" ")?.[1] || "");
				const startTime = rawTime.length >= 5 ? rawTime.slice(0, 5) : rawTime;
				const tid = item.therapist_id ?? item.therapist ?? item.staff_id;
				originalMap.set(Number(item.id), {
					service_id: Number(item.service_id),
					therapist_id: tid != null ? Number(tid) : null,
					start_time: startTime,
				});
			}
		}
		const normalizeTherapist = (svc) => {
			if (svc == null) return null;
			const v = svc.therapist ?? svc.selected_therapist?.value ?? svc.selected_therapist?.id ?? null;
			return v != null ? Number(v) : null;
		};
		const normalizeStartTime = (t) => {
			if (!t) return "";
			const s = String(t).trim();
			const parts = s.split(":");
			if (parts.length >= 2) {
				const h = String(parseInt(parts[0], 10) || 0).padStart(2, "0");
				const m = String(parseInt(parts[1], 10) || 0).padStart(2, "0");
				return `${h}:${m}`;
			}
			return s.length >= 5 ? s.slice(0, 5) : s;
		};
		const allCurrentServices = [
			...(Array.isArray(selectedServices) ? selectedServices : []),
			...(Array.isArray(paxes) ? paxes.flatMap((p) => p.selectedServices || []) : []),
		];
		const originalCount = originalMap.size;
		const currentExistingIds = new Set(allCurrentServices.filter((s) => s.booking_item_id != null).map((s) => Number(s.booking_item_id)));
		const currentNewCount = allCurrentServices.filter((s) => s.booking_item_id == null && !s.isSplitSegment).length;
		if (originalCount !== currentExistingIds.size || currentNewCount > 0) return true;
		// Compare using BASE services only (exclude split segments - they share booking_item_id but have different times)
		for (const [origId, orig] of originalMap) {
			if (!currentExistingIds.has(origId)) return true;
			const curr = allCurrentServices.find((s) =>
				Number(s.booking_item_id) === origId && !s.isSplitSegment
			) || allCurrentServices.find((s) => Number(s.booking_item_id) === origId);
			if (!curr) return true;
			if (Number(curr.service || curr.id) !== orig.service_id) return true;
			if (normalizeTherapist(curr) !== orig.therapist_id) return true;
			if (normalizeStartTime(curr.start_time) !== normalizeStartTime(orig.start_time)) return true;
		}
		return false;
	})();

	const editDateForPayload =
		parseServiceAtDate(itemData?.service_at) ||
		parseServiceAtDate(editedItemData?.service_at) ||
		editBooking?.date ||
		resolvedEditDate;
	const resolvePayloadMembership = () => {
		const value = customerData?.membership ?? editedItemData?.membership ?? itemData?.membership ?? (isMembershipApplied ? 1 : 0);
		return Number(value) === 1 ? 1 : 0;
	};
	const updateData = {
		id: editableBooking?.id,
		...itemData,
		...editedItemData,
		service_at:
			editDateForPayload && editBooking.time?.value ? 
			`${formatDateToYMD(editDateForPayload)} ${editBooking.time.value}`
			: editedItemData.service_at,
		outlet: editBooking.outlet || editableBooking?.outlet_id || selectedOutlet,
		trash: deletedBookingItemIds.join(","),
		customer: customerData?.id ?? editedItemData?.customer ?? itemData?.customer,
		customer_name: customerData?.name ?? editedItemData?.customer_name ?? itemData?.customer_name,
		customer_lastname: customerData?.last_name ?? customerData?.lastname ?? editedItemData?.customer_lastname ?? itemData?.customer_lastname,
		customer_email: customerData?.email ?? editedItemData?.customer_email ?? itemData?.customer_email,
		mobile_number: customerData?.contact_number ?? editedItemData?.mobile_number ?? itemData?.mobile_number,
		membership: resolvePayloadMembership(),
		updated_by: loggedUser,
		isCommissionUpdate: isCommissionUpdate ? 1 : 0,
		// Include update_scope and recurring_group_id for repeat bookings
		...(repeatBooking && editedItemData?.update_scope ? { update_scope: editedItemData.update_scope } : {}),
		...(repeatBooking && editedItemData?.recurring_group_id ? { recurring_group_id: editedItemData.recurring_group_id } : {}),
	};
	
	// Handle update booking
	const handleUpdate = async () => {
		// Check if this is a repeat booking and scope hasn't been selected yet
		if (repeatBooking && !repeatBookingOptionSelected) {
			setShowUpdateRepeatBooking(true);
			setIsSubmitting(false);
			return;
		}
		if (!validateEditFields()) {
			setIsSubmitting(false);
			return;
		}


		// if (setIsBookingSubmitting) setIsBookingSubmitting(true);
		setButtonDisabled(true);
		setIsSubmitting(true);
		// e.preventDefault();
		setApiError("");
		setApiErrorForCreateBooking("")
		setSaveValidationError("");

		// Build items fresh from current state to avoid stale therapist values
		const normalizeTherapistId = (raw) => {
			if (raw == null) return null;
			if (typeof raw === 'object') {
				const val = raw?.value ?? raw?.id ?? raw?.label ?? null;
				if (val == null) return null;
				const n = Number(val);
				return Number.isNaN(n) ? null : n;
			}
			const n = Number(raw);
			return Number.isNaN(n) ? null : n;
		};

		const normTherapist = (svc) => {
			if (svc == null) return null;
			if (typeof svc === 'object') {
				return svc.value ?? svc.id ?? svc.label ?? null;
			}
			return Number(svc);
		};

		// Drag updates Home overrides, but sidebar selectedServices can be overwritten by
		// stale hydrate. Re-apply editPrefill overrides so Save posts the drop values.
		const servicesForSave = prefill ? (Array.isArray(selectedServices) ? selectedServices.map((svc) => applyOverrideToService(svc, prefill)) : []) : (Array.isArray(selectedServices) ? selectedServices : []);
		const paxesForSave = prefill ? (Array.isArray(paxes) ? paxes.map((pax) => ({...pax, selectedServices: Array.isArray(pax.selectedServices) ? pax.selectedServices.map((svc) => applyOverrideToService(svc, prefill)) : pax.selectedServices, })): []) : (Array.isArray(paxes) ? paxes : []);

		const groups = {};
		(Array.isArray(servicesForSave) ? servicesForSave : []).forEach((svc) => {
			const baseKey = svc.splitParent || svc.counterKey;
			if (!groups[baseKey]) {
				groups[baseKey] = [];
			}
			groups[baseKey].push(svc);
		});
		(Array.isArray(paxesForSave) ? paxesForSave : []).forEach((p) => {
			p.selectedServices?.forEach((svc) => {
				const baseKey = svc.splitParent || svc.counterKey;
				if (!groups[baseKey]) {
					groups[baseKey] = [];
				}
				groups[baseKey].push(svc);
			});
		});

		for (const [baseKey, group] of Object.entries(groups || {})) {
			if (!Array.isArray(group) || group.length === 0) continue;
			const base = group.find((s) => !s.isSplitSegment) || group[0];
			// Require base therapist
			if (normTherapist(base?.therapist) == null) {
				setSaveValidationError('Please select therapist for the base service');
				setIsSubmitting(false);
				return;
			}
			if (group.length > 1) {
				// All split segments must have therapist and valid duration/time
				const splitSegs = group.filter((s) => s.isSplitSegment);
				
				// Check therapist selection for all split segments
				if (splitSegs.some((s) => normTherapist(s?.therapist) == null)) {
					setSaveValidationError('Please select therapist for all split segments');
					setIsSubmitting(false);
					return;
				}
				
				// Check duration and time for all split segments
				if (splitSegs.some((s) => {
					const duration = Number(s.duration || 0);
					const hasValidTime = s.start_time && s.start_time !== '00:00' && s.start_time !== '';
					return duration === 0 || !hasValidTime;
				})) {
					setSaveValidationError('Please select duration and time for all split segments');
					setIsSubmitting(false);
					return;
				}
			}
		}

		const mainCustomerName = (customerData?.name || customerData?.firstname || editableBooking?.user?.[0]?.name || '').toString();
		
		// Get guest check-in tag for primary customer
		const customerGuestCheckin = getGuestCheckinPayload('customer');
		
		const normalizedEditBaseDate = formatDateToDDMMYYYY(editDateForPayload);
		let nextMainBaseDate = normalizedEditBaseDate;
		const latestMainItems = (Array.isArray(servicesForSave) ? servicesForSave : []).map((svc) => {
			const baseSvc = (servicesForSave || []).find((s) => !s.isSplitSegment && String(s.counterKey) === String(svc.splitParent || svc.counterKey));
			const isSplit = !!svc.isSplitSegment;
			// New services should send id: null; existing services keep their own id. Splits omit id.
			const idVal = isSplit ? undefined : (svc.booking_item_id != null ? svc.booking_item_id : null);
			const lockedPricing = getLockedBookingItemPricing(svc);
			const serviceMembershipApplied = lockedPricing
				? lockedPricing.isMembershipApplied
				: resolveSavedBooleanFlag(svc?.isMembershipApplied, isMembershipApplied);
			// Keep payload price aligned with live service state (manual/drag recalculated price).
			// Fallback to catalog/base values only when service.price is missing.
			const candidatePrice = svc.price ?? (
				serviceMembershipApplied
					? (svc.member_price ?? baseSvc?.member_price ?? baseSvc?.price)
					: (baseSvc?.price ?? svc.member_price ?? baseSvc?.member_price)
			);
			const resolvedMemberRateApplied = lockedPricing
				? lockedPricing.isMemberRateApplied
				: svc?.isMemberRateApplied;
			const resolvedMembershipApplied = lockedPricing
				? lockedPricing.isMembershipApplied
				: svc?.isMembershipApplied;
			const resolvedPrice = lockedPricing && lockedPricing.price !== undefined
				? lockedPricing.price
				: String((candidatePrice == null || candidatePrice === '') ? 0 : candidatePrice);
			// Build room_segments from room or room_segments on service
			const startTime = extractHHMM(svc.start_time);
			const durationMin = Number(svc.duration);
			const endTimeCalc = startTime && durationMin ? extractHHMM(calculateEndTime(startTime, durationMin)) : extractHHMM(svc.end_time);
			const explicitDayOffset = typeof svc?.dayOffset === "number"
				? svc.dayOffset
				: (typeof svc?.absoluteMinutes === "number" ? Math.floor(svc.absoluteMinutes / 1440) : 0);
			const normalizedSvcStartDate = normalizeDateString(svc?.start_date);
			const signedSvcDateOffset = getDateOffsetFromBase(normalizedSvcStartDate, normalizedEditBaseDate);
			const shouldForceBaseDateFromEdit =
				!!normalizedEditBaseDate &&
				!!normalizedSvcStartDate &&
				signedSvcDateOffset > 0 &&
				explicitDayOffset === 0;
			
			const persistedCrossDaySegments = Array.isArray(baseSvc?.cross_day_booking_item)
				? baseSvc.cross_day_booking_item
				: [];
			// Prefer segment clock times when base row omits end_time (common for 06:00 boundary splits);
			// otherwise preservePersistedCrossDay fails and we rebuild with start_date===end_date, which
			// routes through buildEarlyMorningBoundarySplitItems and shifts segment calendar dates back a day.
			const persistedStartTime = extractHHMM(
				baseSvc?.start_time ||
				baseSvc?.service_at?.split(" ")[1] ||
				persistedCrossDaySegments[0]?.start_time ||
				""
			);
			const persistedEndTime = extractHHMM(
				baseSvc?.end_time ||
				(persistedCrossDaySegments.length > 0
					? persistedCrossDaySegments[persistedCrossDaySegments.length - 1]?.end_time
					: "") ||
				""
			);
			const mainDateRange = shouldForceBaseDateFromEdit
				? buildDateRangeFromResolvedStartDate(normalizedEditBaseDate, startTime, endTimeCalc)
				: getPayloadDateRangeForEditSave(
					svc,
					baseSvc,
					startTime,
					endTimeCalc,
					nextMainBaseDate
				);
			// Only ever preserve a persisted cross_day_booking_item when it represents a genuine
			const isGenuinePersistedCrossDaySegments =
				persistedCrossDaySegments.length >= 2 &&
				!hasExpandedCoverageSegments({ cross_day_booking_item: persistedCrossDaySegments }) &&
				!hasDegenerateZeroDurationSegment(persistedCrossDaySegments) &&
				!isDualCalendarDisplayCrossDayForEditPayload(persistedCrossDaySegments);
			const persistedCrossDayDateRange = isGenuinePersistedCrossDaySegments
				? getSegmentDateRange(persistedCrossDaySegments)
				: null;
			const preservePersistedCrossDay =
				svc?.booking_item_id != null &&
				isGenuinePersistedCrossDaySegments &&
				startTime === persistedStartTime &&
				endTimeCalc === persistedEndTime &&
				!shouldForceBaseDateFromEdit &&
				(
					!persistedCrossDayDateRange ||
					(
						normalizeDateString(persistedCrossDayDateRange.start_date) === normalizeDateString(mainDateRange.start_date) &&
						normalizeDateString(persistedCrossDayDateRange.end_date) === normalizeDateString(mainDateRange.end_date)
					)
				);
			const preservedCrossDayDateRange = preservePersistedCrossDay
				? persistedCrossDayDateRange
				: null;
			const segmentAnchoredRangeForRebuild =
				!preservePersistedCrossDay &&
				isGenuinePersistedCrossDaySegments &&
				isDualCalendarDisplayCrossDayForEditPayload(persistedCrossDaySegments) &&
				startTime === persistedStartTime &&
				endTimeCalc === persistedEndTime
					? getSegmentDateRange(persistedCrossDaySegments)
					: null;
			const payloadCrossDayDateRange =
				segmentAnchoredRangeForRebuild?.start_date && segmentAnchoredRangeForRebuild?.end_date
					? segmentAnchoredRangeForRebuild
					: mainDateRange;
			const calculatedStartDate =
				preservedCrossDayDateRange?.start_date ??
				payloadCrossDayDateRange.start_date;
			const calculatedEndDate =
				preservedCrossDayDateRange?.end_date ??
				payloadCrossDayDateRange.end_date;
			const rebuiltCrossDayBookingItem = preservePersistedCrossDay
				? persistedCrossDaySegments
				: buildPayloadCrossDayBookingItem(
					payloadCrossDayDateRange.start_date,
					payloadCrossDayDateRange.end_date,
					startTime,
					endTimeCalc
				);
			const forceCalendarExpansionForEditPayload =
				isDualCalendarDisplayCrossDayForEditPayload(rebuiltCrossDayBookingItem);
			// calculatedStartDate/calculatedEndDate already reflect the correct dates for this
			// service (including a midnight rollover for services ending exactly at "00:00"),
			// regardless of whether a cross_day_booking_item was actually built for it — a
			// service can legitimately span two calendar dates without being a "genuine split".
			const effectiveDateRange = normalizeEarlyMorningPayloadDateRange(
				calculatedStartDate,
				calculatedEndDate,
				startTime,
				endTimeCalc
			);
			const effectiveStartDate = effectiveDateRange.start_date;
			const effectiveEndDate = effectiveDateRange.end_date;
			nextMainBaseDate = effectiveEndDate;
			const roomSegments = (() => {
				if (Array.isArray(svc.room_segments) && svc.room_segments.length > 0) {
					return svc.room_segments.map((seg, idx) => {
						const rid = seg.room_id ?? seg.id ?? svc.roomId ?? svc.room ?? svc.roomData?.id ?? null;
						const itype = seg.item_type
							?? seg.item
							?? svc.roomData?.item_type
							?? svc.roomData?.matchedItem?.item
							?? svc.room?.item_type
							?? svc.item_type
							?? svc.room_name
							?? null;
						return {
							room_id: rid,
							item_type: itype,
							meta_service: seg.meta_service ?? null,
							start_time: startTime,
							end_time: endTimeCalc,
							duration: durationMin,
							priority: Number(seg.priority ?? (idx + 1)),
							facilities: Array.isArray(seg.facilities) ? seg.facilities : [],
							room_type: seg.room_type ?? null,
						};
					});
				}
				// Build from single room fields
				const rid = svc.roomId ?? svc.room?.value ?? svc.room ?? baseSvc?.roomId ?? baseSvc?.room ?? svc.roomData?.id ?? null;
				const itype = svc.roomData?.item_type
					?? svc.roomData?.matchedItem?.item
					?? svc.room?.item_type
					?? svc.item_type
					?? baseSvc?.item_type
					?? svc.room_name
					?? null;
				if (rid != null) {
					return [{
						room_id: rid,
						item_type: itype,
						meta_service: null,
						start_time: startTime,
						end_time: endTimeCalc,
						duration: durationMin,
						priority: 1,
					}];
				}
				return null;
			})();
			const below12 = customerGuestCheckin.hasSelection ? customerGuestCheckin.below12 : Number(svc.below_12 ?? baseSvc?.below_12 ?? 0);
			const below12Payload = below12 === 1 ? { below_12: 1 } : {};
			const guestCheckinId = below12 === 1
				? null
				: (customerGuestCheckin.hasSelection ? customerGuestCheckin.guestCheckinId : (svc.guest_checkin_id ?? baseSvc?.guest_checkin_id ?? null));
			const guestCheckinName = below12 === 1
				? null
				: (customerGuestCheckin.hasSelection ? customerGuestCheckin.guestCheckinName : (svc.guest_checkin_name ?? baseSvc?.guest_checkin_name ?? null));
			const baseObj = {
			service: Number(svc.service || svc.id),
			customer_name: svc.customer_name || mainCustomerName,
			start_time: extractHHMM(svc.start_time),
			end_time: endTimeCalc,
			duration: Number(svc.duration),
			therapist: normalizeTherapistId(svc.therapist),
			// room replaced by room_segments per backend expectation
			room_segments: roomSegments,
			requested_person: (typeof svc.requested_person === 'number') ? svc.requested_person : (svc.requested_therapist ? 1 : 0),
			requested_room: svc.requested_room ? 1 : 0,
			isMemberRateApplied: resolvedMemberRateApplied,
			isMembershipApplied: resolvedMembershipApplied,
			price: resolvedPrice,
			quantity: '1',
			service_request: svc.service_request || '',
			commission: { tier: null, title: null, rate: "0.00" },
			primary: 1,
			item_number: 1,
			splited: svc.splited,
			parent_booking_item_id: svc.parent_booking_item_id || null,
			cross_day_booking_item: rebuiltCrossDayBookingItem,
			start_date: effectiveStartDate,
			end_date: effectiveEndDate,
			// Only a genuine split (caught above by forceCalendarExpansionForEditPayload, or a
			// preserved genuine persisted split) should ever be sent as is_calendar_expansion: 1.
			// Do not fall back to echoing a stale raw flag or a same-vs-base-date offset — that is
			// exactly what caused non-split bookings to keep coming back as calendar_expansion: 1.
			is_calendar_expansion: forceCalendarExpansionForEditPayload
				? 1
				: preservePersistedCrossDay
				? getItemCalendarExpansionFlag(baseSvc || svc, effectiveStartDate)
				: 0,
			guest_checkin_id: guestCheckinId,
			guest_checkin_name: guestCheckinName,
			...below12Payload,
			addons: (() => {
				const raw = svc.addons;
				if (!Array.isArray(raw) || raw.length === 0) return [];
				return raw.map((a) => ({
					id: Number(a.id ?? a.addon_id ?? a.item_id ?? 0),
					name: String(a.name ?? a.addon_name ?? a.item_name ?? ""),
					quantity: Number(a.quantity ?? 1) || 1,
					unit_price: a?.unit_price,
					booking_item_addon_id: a?.booking_item_addon_id,
				})).filter((a) => a.id && a.name);
			})(),
			};
			return idVal !== undefined ? { id: idVal, ...baseObj } : baseObj;
			});

		const firstPaxBaseDate = latestMainItems.length > 0
			? (latestMainItems[latestMainItems.length - 1]?.end_date || latestMainItems[latestMainItems.length - 1]?.start_date || normalizedEditBaseDate)
			: normalizedEditBaseDate;
		const latestPaxItems = (Array.isArray(paxesForSave) ? paxesForSave : []).flatMap((p) => {
			const paxServices = Array.isArray(p.selectedServices) ? p.selectedServices : [];
			const paxGuestNum = Number(String(p.id || "").replace(/\D/g, "")) || 1;
			const paxName = p.customer_name || `Guest${paxGuestNum}`;
			const idx = paxGuestNum + 1;
			let nextPaxBaseDate = firstPaxBaseDate;
			
			// Get guest check-in tag for this pax
			const paxGuestCheckin = getGuestCheckinPayload(p.id);
			
			return paxServices.map((svc) => {
				const baseKey = svc.splitParent || svc.counterKey;
				// For pax services, look up base service from the same pax's services, not selectedServices
				const baseSvc = (paxServices || []).find((s) => !s.isSplitSegment && String(s.counterKey) === String(baseKey));
				const isSplit = !!svc.isSplitSegment;
				// New services should send id: null; existing services keep their own id. Splits omit id.
				const idVal = isSplit ? undefined : (svc.booking_item_id != null ? svc.booking_item_id : null);
				const lockedPricing = getLockedBookingItemPricing(svc);
				const serviceMembershipApplied = lockedPricing
					? lockedPricing.isMembershipApplied
					: resolveSavedBooleanFlag(svc?.isMembershipApplied, isMembershipApplied);
				// Keep payload price aligned with live service state (manual/drag recalculated price).
				// Fallback to catalog/base values only when service.price is missing.
				const candidatePrice = svc.price ?? (
					serviceMembershipApplied
						? (svc.member_price ?? baseSvc?.member_price ?? baseSvc?.price)
						: (baseSvc?.price ?? svc.member_price ?? baseSvc?.member_price)
				);
				const resolvedMemberRateApplied = lockedPricing
					? lockedPricing.isMemberRateApplied
					: svc?.isMemberRateApplied;
				const resolvedMembershipApplied = lockedPricing
					? lockedPricing.isMembershipApplied
					: svc?.isMembershipApplied;
				const resolvedPrice = lockedPricing && lockedPricing.price !== undefined
					? lockedPricing.price
					: String((candidatePrice == null || candidatePrice === '') ? 0 : candidatePrice);
				// Build room_segments from room or room_segments on service
				const startTime = extractHHMM(svc.start_time);
				const durationMin = Number(svc.duration);
				const endTimeCalc = startTime && durationMin ? extractHHMM(calculateEndTime(startTime, durationMin)) : extractHHMM(svc.end_time);
				const explicitDayOffset = typeof svc?.dayOffset === "number"
					? svc.dayOffset
					: (typeof svc?.absoluteMinutes === "number" ? Math.floor(svc.absoluteMinutes / 1440) : 0);
				const normalizedSvcStartDate = normalizeDateString(svc?.start_date);
				const signedSvcDateOffset = getDateOffsetFromBase(normalizedSvcStartDate, normalizedEditBaseDate);
				const shouldForceBaseDateFromEdit =
					!!normalizedEditBaseDate &&
					!!normalizedSvcStartDate &&
					signedSvcDateOffset > 0 &&
					explicitDayOffset === 0;
				
				const persistedCrossDaySegments = Array.isArray(baseSvc?.cross_day_booking_item)
					? baseSvc.cross_day_booking_item
					: [];
				const persistedStartTime = extractHHMM(
					baseSvc?.start_time ||
					baseSvc?.service_at?.split(" ")[1] ||
					persistedCrossDaySegments[0]?.start_time ||
					""
				);
				const persistedEndTime = extractHHMM(
					baseSvc?.end_time ||
					(persistedCrossDaySegments.length > 0
						? persistedCrossDaySegments[persistedCrossDaySegments.length - 1]?.end_time
						: "") ||
					""
				);
				const paxDateRange = shouldForceBaseDateFromEdit
					? buildDateRangeFromResolvedStartDate(normalizedEditBaseDate, startTime, endTimeCalc)
					: getPayloadDateRangeForEditSave(
						svc,
						baseSvc,
						startTime,
						endTimeCalc,
						nextPaxBaseDate
					);
				// Same rule as the main-customer builder above: only preserve a persisted
				// cross_day_booking_item when it is a genuine multi-segment split with different
				// clock times and real duration on both days. Leftover single/duplicate/degenerate
				// segments must never be preserved or force is_calendar_expansion to 1.
				const isGenuinePersistedCrossDaySegments =
					persistedCrossDaySegments.length >= 2 &&
					!hasExpandedCoverageSegments({ cross_day_booking_item: persistedCrossDaySegments }) &&
					!hasDegenerateZeroDurationSegment(persistedCrossDaySegments) &&
					!isDualCalendarDisplayCrossDayForEditPayload(persistedCrossDaySegments);
				const persistedCrossDayDateRange = isGenuinePersistedCrossDaySegments
					? getSegmentDateRange(persistedCrossDaySegments)
					: null;
				const preservePersistedCrossDay =
					svc?.booking_item_id != null &&
					isGenuinePersistedCrossDaySegments &&
					startTime === persistedStartTime &&
					endTimeCalc === persistedEndTime &&
					!shouldForceBaseDateFromEdit &&
					(
						!persistedCrossDayDateRange ||
						(
							normalizeDateString(persistedCrossDayDateRange.start_date) === normalizeDateString(paxDateRange.start_date) &&
							normalizeDateString(persistedCrossDayDateRange.end_date) === normalizeDateString(paxDateRange.end_date)
						)
					);
				const preservedCrossDayDateRange = preservePersistedCrossDay
					? persistedCrossDayDateRange
					: null;
				const segmentAnchoredRangeForRebuildPax =
					!preservePersistedCrossDay &&
					isGenuinePersistedCrossDaySegments &&
					isDualCalendarDisplayCrossDayForEditPayload(persistedCrossDaySegments) &&
					startTime === persistedStartTime &&
					endTimeCalc === persistedEndTime
						? getSegmentDateRange(persistedCrossDaySegments)
						: null;
				const payloadCrossDayDateRangePax =
					segmentAnchoredRangeForRebuildPax?.start_date && segmentAnchoredRangeForRebuildPax?.end_date
						? segmentAnchoredRangeForRebuildPax
						: paxDateRange;
				const calculatedStartDate =
					preservedCrossDayDateRange?.start_date ??
					payloadCrossDayDateRangePax.start_date;
				const calculatedEndDate =
					preservedCrossDayDateRange?.end_date ??
					payloadCrossDayDateRangePax.end_date;
				const rebuiltCrossDayBookingItem = preservePersistedCrossDay
					? persistedCrossDaySegments
					: buildPayloadCrossDayBookingItem(
						payloadCrossDayDateRangePax.start_date,
						payloadCrossDayDateRangePax.end_date,
						startTime,
						endTimeCalc
					);
				const forceCalendarExpansionForEditPayload =
					isDualCalendarDisplayCrossDayForEditPayload(rebuiltCrossDayBookingItem);
				// calculatedStartDate/calculatedEndDate already reflect the correct dates for this
				// service (including a midnight rollover for services ending exactly at "00:00"),
				// regardless of whether a cross_day_booking_item was actually built for it.
				const effectiveDateRange = normalizeEarlyMorningPayloadDateRange(
					calculatedStartDate,
					calculatedEndDate,
					startTime,
					endTimeCalc
				);
				const effectiveStartDate = effectiveDateRange.start_date;
				const effectiveEndDate = effectiveDateRange.end_date;
				nextPaxBaseDate = effectiveEndDate;
				const roomSegments = (() => {
					if (Array.isArray(svc.room_segments) && svc.room_segments.length > 0) {
						return svc.room_segments.map((seg, sidx) => {
							const rid = seg.room_id ?? seg.id ?? svc.roomId ?? svc.room ?? svc.roomData?.id ?? null;
							const itype = seg.item_type
								?? seg.item
								?? svc.roomData?.item_type
								?? svc.roomData?.matchedItem?.item
								?? svc.room?.item_type
								?? svc.item_type
								?? svc.room_name
								?? null;
							return {
								room_id: rid,
								item_type: itype,
								meta_service: seg.meta_service ?? null,
								start_time: startTime,
								end_time: endTimeCalc,
								duration: durationMin,
								priority: Number(seg.priority ?? (sidx + 1)),
								facilities: Array.isArray(seg.facilities) ? seg.facilities : [],
								room_type: seg.room_type ?? null,
							};
						});
					}
					const rid = svc.roomId ?? svc.room?.value ?? svc.room ?? baseSvc?.roomId ?? baseSvc?.room ?? svc.roomData?.id ?? null;
					const itype = svc.roomData?.item_type
						?? svc.roomData?.matchedItem?.item
						?? svc.room?.item_type
						?? svc.item_type
						?? baseSvc?.item_type
						?? svc.room_name
						?? null;
					if (rid != null) {
						return [{
							room_id: rid,
							item_type: itype,
							meta_service: null,
							start_time: startTime,
							end_time: endTimeCalc,
							duration: durationMin,
							priority: 1,
							facilities: Array.isArray(svc.facilities) ? svc.facilities : [],
							room_type: svc.room_type ?? null,
						}];
					}
					return null;
				})();
				const below12 = paxGuestCheckin.hasSelection ? paxGuestCheckin.below12 : Number(svc.below_12 ?? baseSvc?.below_12 ?? 0);
				const below12Payload = below12 === 1 ? { below_12: 1 } : {};
				const guestCheckinId = below12 === 1
					? null
					: (paxGuestCheckin.hasSelection ? paxGuestCheckin.guestCheckinId : (svc.guest_checkin_id ?? baseSvc?.guest_checkin_id ?? null));
				const guestCheckinName = below12 === 1
					? null
					: (paxGuestCheckin.hasSelection ? paxGuestCheckin.guestCheckinName : (svc.guest_checkin_name ?? baseSvc?.guest_checkin_name ?? null));
				const baseObj = {
				service: Number(svc.service || svc.id),
				customer_name: paxName,
				start_time: extractHHMM(svc.start_time),
				end_time: endTimeCalc,
				duration: Number(svc.duration),
				therapist: normalizeTherapistId(svc.therapist),
				room_segments: roomSegments,
				requested_person: (typeof svc.requested_person === 'number') ? svc.requested_person : (svc.requested_therapist ? 1 : 0),
				requested_room: svc.requested_room ? 1 : 0,
				isMemberRateApplied: resolvedMemberRateApplied,
				isMembershipApplied: resolvedMembershipApplied,
				price: resolvedPrice,
				quantity: '1',
				service_request: svc.service_request || '',
				commission: { tier: null, title: null, rate: "0.00" },
				primary: 0,
				item_number: idx,
				splited: svc.splited,
				parent_booking_item_id: svc.parent_booking_item_id || null,
				cross_day_booking_item: rebuiltCrossDayBookingItem,
				start_date: effectiveStartDate,
				end_date: effectiveEndDate,
				is_calendar_expansion: forceCalendarExpansionForEditPayload
					? 1
					: preservePersistedCrossDay
					? getItemCalendarExpansionFlag(baseSvc || svc, effectiveStartDate)
					: 0,
				guest_checkin_id: guestCheckinId,
				guest_checkin_name: guestCheckinName,
				...below12Payload,
				addons: (() => {
					const raw = svc.addons;
					if (!Array.isArray(raw) || raw.length === 0) return [];
					return raw.map((a) => ({
						id: Number(a.id ?? a.addon_id ?? a.item_id ?? 0),
						name: String(a.name ?? a.addon_name ?? a.item_name ?? ""),
						quantity: Number(a.quantity ?? 1) || 1,
						unit_price: a?.unit_price,
						booking_item_addon_id: a?.booking_item_addon_id,
					})).filter((a) => a.id && a.name);
				})(),
				};
				return idVal !== undefined ? { id: idVal, ...baseObj } : baseObj;
				});
		});

		const latestItems = [...latestMainItems, ...latestPaxItems];
		const currentEditedServices = [
			...(Array.isArray(servicesForSave) ? servicesForSave : []),
			...(Array.isArray(paxesForSave)
				? paxesForSave.flatMap((pax) => (Array.isArray(pax?.selectedServices) ? pax.selectedServices : []))
				: []),
		];
		const desiredDateByItemId = new Map();
		currentEditedServices.forEach((service) => {
			const idCandidate = service?.booking_item_id ?? service?.id ?? null;
			if (idCandidate == null) return;
			const startDate = normalizeDateString(service?.start_date) || normalizedEditBaseDate;
			const endDate = normalizeDateString(service?.end_date) || startDate;
			desiredDateByItemId.set(String(idCandidate), {
				start_date: startDate,
				end_date: endDate,
			});
		});
		const reconciledLatestItems = latestItems.map((item) => {
			const itemId = item?.id ?? null;
			if (itemId == null) return item;
			const desired = desiredDateByItemId.get(String(itemId));
			if (!desired) return item;
			const startTime = extractHHMM(item?.start_time || "");
			const endTime = extractHHMM(item?.end_time || "");
			const desiredRange = startTime && endTime
				? buildDateRangeFromResolvedStartDate(desired.start_date, startTime, endTime)
				: desired;
			const desiredStartDate = desiredRange.start_date || desired.start_date;
			const desiredEndDate = desiredRange.end_date || desired.end_date || desiredStartDate;
			const currentStart = normalizeDateString(item?.start_date);
			const currentEnd = normalizeDateString(item?.end_date) || currentStart;
			if (
				currentStart === desiredStartDate &&
				currentEnd === desiredEndDate
			) {
				return item;
			}
			return {
				...item,
				start_date: desiredStartDate,
				end_date: desiredEndDate,
				cross_day_booking_item:
					startTime && endTime
						? buildPayloadCrossDayBookingItem(
							desiredStartDate,
							desiredEndDate,
							startTime,
							endTime
						)
						: item?.cross_day_booking_item ?? null,
			};
		});
		const firstStartDate = reconciledLatestItems[0]?.start_date || null;
		const hasCrossDayService = reconciledLatestItems.some((item) =>
			item?.start_date &&
			item?.end_date &&
			item.start_date !== item.end_date
		);
		const hasCrossDayBookingItems = reconciledLatestItems.some((item) =>
			Array.isArray(item?.cross_day_booking_item) &&
			item.cross_day_booking_item.length > 0
		);
		const hasDifferentStartDates = reconciledLatestItems.some((item) =>
			item?.start_date &&
			firstStartDate &&
			item.start_date !== firstStartDate
		);
		const hasExtendedBoundaryService = reconciledLatestItems.some((item) =>
			isExtendedBoundaryCrossDayService(item)
		);
		const payload = {
			...updateData,
			items: JSON.stringify(reconciledLatestItems),
			cross_day_booking: (hasCrossDayService || hasCrossDayBookingItems || hasDifferentStartDates || hasExtendedBoundaryService) ? 1 : 0,
		};
		// Booking-level `service_at` must match the first line’s real start (items[].start_date +
		// start_time). Using only sidebar `resolvedEditDate` + time sends e.g. 22 May 23:30 while
		// the first segment is 21 May 23:30; the API then rewrites `service_date` and the grid can
		// jump (list vs detail, or card stacking).
		if (reconciledLatestItems.length > 0) {
			const firstLine = reconciledLatestItems[0];
			const startDateStr = firstLine?.start_date;
			const startTimeStr = firstLine?.start_time;
			if (startDateStr && startTimeStr) {
				const parsedFirst = parseServiceAtDate(startDateStr);
				if (parsedFirst) {
					const ymd = formatDateToYMD(parsedFirst);
					const hhmm = String(startTimeStr).trim();
					const hhmmss = hhmm.length === 5 ? `${hhmm}:00` : hhmm;
					if (ymd && hhmmss) {
						payload.service_at = `${ymd} ${hhmmss}`;
					}
				}
			}
		}
		try {
			const response = await editBookingData(payload.id, payload);
			if (response?.data?.success) {
				const updatedBooking = response?.data?.data?.booking;
				const expansionByItemId = new Map();
				reconciledLatestItems.forEach((item) => {
					const itemId = item?.id;
					if (itemId == null) return;
					expansionByItemId.set(String(itemId), item?.is_calendar_expansion);
				});
				const liveServicesByItemId = new Map();
				[
					...(Array.isArray(selectedServices) ? selectedServices : []),
					...(Array.isArray(paxes) ? paxes.flatMap((pax) => (Array.isArray(pax?.selectedServices) ? pax.selectedServices : [])) : []),
				].forEach((svc) => {
					const itemId = svc?.booking_item_id ?? svc?.item_id;
					if (itemId != null) liveServicesByItemId.set(String(itemId), svc);
				});
				const enrichedUpdatedBooking = updatedBooking?.booking_item
					? {
						...updatedBooking,
						booking_item: Object.fromEntries(
							Object.entries(updatedBooking.booking_item).map(([customerName, items]) => [
								customerName,
								(Array.isArray(items) ? items : []).map((item) => {
									const itemId = item?.id ?? item?.booking_item_id;
									const savedExpansion = itemId != null ? expansionByItemId.get(String(itemId)) : undefined;
									const liveService = itemId != null ? liveServicesByItemId.get(String(itemId)) : null;
									const liveCategory = liveService?.service_category_name || liveService?.service_category || "";
									return {
										...item,
										...(savedExpansion != null ? { is_calendar_expansion: savedExpansion } : {}),
										service_category_name: liveCategory || item?.service_category_name || "",
										service_category: liveCategory || item?.service_category || "",
									};
								}),
							])
						),
					}
					: updatedBooking;
				const updatedApiId =
					enrichedUpdatedBooking?.booking_id ?? enrichedUpdatedBooking?.id ?? response?.data?.data?.id;
				setBookingsList((prev) => {
					const filtered = prev.filter(
						(b) => String(b.booking_id || b.id) !== String(updatedApiId)
					);
					return [...filtered, enrichedUpdatedBooking];
				});
				const updatedBookingID = response?.data?.data.id;
				setSelectedBookingId(updatedBookingID);
				setSelectedGuestCheckin({});
				const bookingIdForOverrideClear = String(
					updatedBookingID ?? editableBooking?.id ?? payload?.id ?? ""
				);
				// Clearing dragOverrides in the same tick as setBookingsList paints one frame from
				// stale filteredBookings → card snaps to normal time then back to +1. Wait until the
				// grid has regrouped the saved booking before dropping live edit overrides.
				const scheduleCancelEditOverride = () => {
					try {
						window.dispatchEvent(
							new CustomEvent("cancelEditOverride", {
								detail: { bookingId: bookingIdForOverrideClear },
							})
						);
					} catch {}
				};
				setTimeout(scheduleCancelEditOverride, 120);
				if (typeof onUpdate === "function") {
					try { onUpdate(updatedBookingID); } catch {}
				}
				scrollToBooking(updatedBookingID);
				setShowBookingSummary(true);
				setIsBookingInfoUpdated(true);
				setNewAppointmentSlots([]);
				setShowConflictModal(false);
				// setTriggerCancel(true);
				setIsNewBookingSlotActive(false);
				// Unlock header immediately; never leave create-booking mode from a polluted edit flow.
				setIsEditBookingActive(false);
				setEditableBooking(null);
				setNewBookingActive(false);
				try {setDateSyncState({requestId: 0,mode: null,status: "idle",date: null,});} catch {}

				setItemData(null);
				setSelectedServices([]);
				setPaxes([]);
				setCustomerData({});
				setCreatedBooking(null);
				setSelectedBookingDeleteScope(null);
				setSelectedBookingUpdateScope(null);
				toast.success("Booking updated successfully!");
				// setButtonDisabled(false);
				setIsSubmitting(false);
				setCompleteBookingWarning(false);
				setShowPaymentInProgressNotice(false);

			} else {
				console.error("Booking update failed:", response);
				// Response is API body (e.g. 422): may have message and errors.items
				const errItems = response?.errors?.items;
				const errMsg = Array.isArray(errItems) && errItems.length > 0
					? errItems.join(" ")
					: response?.message;
				const displayMsg = errMsg || "Booking update failed.";
				// Keep edit failures local — do not set global apiError (leaks onto summary after Cancel/Reload)
				setApiErrorForCreateBooking(displayMsg);
				setButtonDisabled(false);
				setIsSubmitting(false);
			}
		} catch (error) {
			console.error("Error updating booking:", error);
			const errData = error?.response?.data ?? error;
			const errItems = errData?.errors?.items;
			const errMsg = Array.isArray(errItems) && errItems.length > 0
				? errItems.join(" ")
				: errData?.message ?? error?.message;
			const displayMsg = errMsg || "Error updating booking.";
			// Keep edit failures local — do not set global apiError (leaks onto summary after Cancel/Reload)
			setApiErrorForCreateBooking(displayMsg);
			setButtonDisabled(false);
			setIsSubmitting(false);
		}
	};

	// Validate that all services have room information
	const allServicesForRoomValidation = [
		...(Array.isArray(selectedServices) ? selectedServices : []),
		...(Array.isArray(paxes)
			? paxes.flatMap((pax) => Array.isArray(pax?.selectedServices) ? pax.selectedServices : [])
			: []),
	];
	const allServicesHaveRooms = Array.isArray(selectedServices) && 
		selectedServices.length > 0 && 
		allServicesForRoomValidation.every(service => {
			// Check if service has room selected (matches ServiceCard error condition exactly)
			// Error shows when: !selectedRooms[service.counterKey]?.label && !service.room_name && !service.room_segments
			const hasSelectedRoom = selectedRooms[service.counterKey]?.label;
			const hasRoomName = service.room_name && service.room_name.trim() !== '';
			const hasRoomSegments = service.room_segments !== null && service.room_segments !== undefined;
			// Service is valid if it has at least one of: selectedRoom label, room_name, or room_segments
			return hasSelectedRoom || hasRoomName || hasRoomSegments;
		});

	// Validate that customer data exists with an ID
	const hasValidCustomer = customerData?.id;
	const canSave = allServicesHaveRooms && hasValidCustomer;

	const allServicesHaveTherapists = allServicesForRoomValidation.every(
		(service) => service?.therapist != null || service?.selected_therapist?.value != null
	);

	useEffect(() => {
		if (allServicesHaveTherapists) {
			setSaveValidationError("");
		}
	}, [allServicesHaveTherapists]);
	const unavailableTherapistName = selectedTherapistFromRow?.alias || selectedTherapistFromRow?.name || selectedServices?.[0]?.selected_therapist?.label || selectedServices?.[0]?.therapist_name ||"Selected therapist";

	return (
		<>
			<div className={styles.rightHeader}>
				<h5>Update Appointment</h5>
				<BorderedButton className={styles.cancelButton} text="Cancel" onClick={onClose} />
			</div>

			
			<div className={styles.divWrapper}>
				<div className={styles.divHeight}>
					<div
						inert={isBookingStatusReadOnly}
						className={isBookingStatusReadOnly ? styles.readOnlyBookingSection : undefined}
					>
					<DateTimeOutletSelector
						fromEdit={true}
						interval={15}
						initialDate={editBooking.date}
						initialTime={editBooking.time}
						initialOutlet={editBooking.outlet}
						// onPaymentInProgressAttempt={() => setShowPaymentInProgressNotice(true)}
						onDateChange={(newDate) => {
							if (prefill) {
								prefillBeforeDateChangeRef.current = prefill;
							}
							setEditBooking((prev) => ({
								...prev,
								date: newDate,
							}));
							setBookingChanged(true);
							// Keep header time on the newly selected base day (no leftover +1 offset).
							setSelectedTime((prev) => {
								if (!prev?.value) return prev;
								const normalized = buildTimeSelection(
									prev.value,
									0,
									convertTo12Hour(prev.value),
									convertToMinutes(prev.value)
								);
								return normalized || prev;
							});
						}}
						onTimeChange={(newTime) => {
							setEditBooking((prev) => ({
								...prev,
								time: newTime
							}));
							setSelectedTime(newTime);
							setBookingChanged(true);
							try {
						// Shared chain algorithm: header time = move first main service (Rule 2).
						setSelectedServices((prev) => {
							if (!Array.isArray(prev) || prev.length === 0) return prev;
							const firstKey = resolveServiceScheduleKey(prev[0]);
							const newAbsoluteStartMinutes =
								typeof newTime?.absoluteMinutes === "number" && !Number.isNaN(newTime.absoluteMinutes)
									? newTime.absoluteMinutes
									: ((newTime?.dayOffset ?? 0) * 1440) + convertToMinutes(newTime?.value || prev[0]?.start_time || "00:00");

							const { services: scheduled, affectedIndices } = scheduleServiceMove({
								services: ensureServiceLinkedFlags(prev),
								movedKey: firstKey,
								newAbsoluteStartMinutes,
							});
							const affectedSet = new Set(affectedIndices);

							const updated = scheduled.map((svc, index) => {
								if (!affectedSet.has(index)) return svc;
								const startTime = svc.start_time;
								const durationMin = Number(svc.duration) || 0;
								const endTime = svc.end_time || calculateEndTime(startTime, durationMin);
								const signedDayOffset =
									typeof svc.absoluteMinutes === "number" && !Number.isNaN(svc.absoluteMinutes)
										? Math.floor(svc.absoluteMinutes / 1440)
										: (svc.dayOffset ?? 0);
								const dateRange = deriveDateRangeFromSelection(
									resolvedEditDate,
									startTime,
									endTime,
									signedDayOffset
								);
								return {
									...svc,
									start_time: startTime,
									end_time: endTime,
									start_date: dateRange.start_date,
									end_date: dateRange.end_date,
									dayOffset: signedDayOffset,
									absoluteMinutes:
										typeof svc.absoluteMinutes === "number" && !Number.isNaN(svc.absoluteMinutes)
											? svc.absoluteMinutes
											: (signedDayOffset * 1440) + convertToMinutes(startTime),
									is_calendar_expansion: signedDayOffset > 0 ? 1 : 0,
									cross_day_booking_item: buildPayloadCrossDayBookingItem(
										dateRange.start_date,
										dateRange.end_date,
										startTime,
										endTime
									),
									hasManualTimeSelection: true,
								};
							});
							manualTimeSyncRef.current = updated;

							// Emit live overrides for all affected chain services
							try {
								if (editableBooking?.id) {
									affectedIndices.forEach((idx) => {
										const svc = updated[idx];
										if (!svc?.booking_item_id) return;
										const roomSelection = svc?.counterKey != null ? selectedRooms?.[svc.counterKey] : null;
										emitProjectedEditOverride({
											bookingId: editableBooking.id,
											itemId: svc.booking_item_id,
											start: svc.start_time,
											end: svc.end_time,
											therapistId: svc.therapist,
											therapistName: svc.therapist_name,
											roomId: svc?.roomId ?? roomSelection?.value ?? svc?.room?.value ?? svc?.room ?? null,
											roomName: svc?.roomName || svc?.room_name || roomSelection?.label || "",
											serviceName: svc?.name || svc?.service_name || "",
											service_category: svc?.service_category_name || svc?.service_category || "",
											service_at: svc.start_date ? `${svc.start_date} ${svc.start_time}` : undefined,
											absoluteStartMinutes: svc.absoluteMinutes,
											duration: svc?.duration,
											linkedToPrevious: svc.linkedToPrevious,
											scheduleLinkParentKey: svc.scheduleLinkParentKey ?? null,
										});
									});
								}
							} catch {}

							return updated;
						});
							try {
								const firstSvc = (selectedServices && selectedServices[0]) || null;
								if (firstSvc?.booking_item_id) {
									setTimeout(() => scrollToBookingCard(firstSvc.booking_item_id, 100), 200);
								}
							} catch {}
							} catch {}
						}}
					/>
					
					<CustomerSelector
						fromEdit={true}
						error={customerError}
						setError={setCustomerError}
						setApiErrorForCreateBooking={setApiErrorForCreateBooking}
						handleGetGuestCheckinList={handleGetGuestCheckinList}
						handleSelectGuestCheckin={handleSelectGuestCheckin}
						selectedGuestCheckin={getSelectedGuestCheckin('customer')}
						guestCheckinList={guestCheckinList}
						guestCheckinRemoteList
						onGuestCheckinSearchQueryChange={scheduleGuestCheckinSearch}
						onGuestCheckinLoadMore={handleGuestCheckinLoadMore}
						guestCheckinHasMore={guestCheckinHasMore}
						guestCheckinLoadingMore={guestCheckinLoadingMore}
					/>

					{repeatBooking && (
						<div className={`${styles.recurringParentWrapper} d-flex`}>
							<RepeatBookingIcon size={15} color="#3C2212"/>
							{editableBooking?.recurring_weeks ? 
							<p>Repeats weekly</p> 
							: 
							<p>Repeats on selected dates</p>
							}
						</div>
					)}

					<ServiceSelector
						fromEdit={true}
						error={serviceError}
						setError={setServiceError}
						interval={interval}
						isOutletExtended={isOutletExtended}
						editBooking={editBooking}
						bookingChanged={bookingChanged}
						// onPaymentInProgressAttempt={() => setShowPaymentInProgressNotice(true)}
						handleGetGuestCheckinList={handleGetGuestCheckinList}
						handleSelectGuestCheckin={handleSelectGuestCheckin}
						getSelectedGuestCheckin={getSelectedGuestCheckin}
						guestCheckinList={guestCheckinList}
						guestCheckinRemoteList
						onGuestCheckinSearchQueryChange={scheduleGuestCheckinSearch}
						onGuestCheckinLoadMore={handleGuestCheckinLoadMore}
						guestCheckinHasMore={guestCheckinHasMore}
						guestCheckinLoadingMore={guestCheckinLoadingMore}
						onRoomConflictChange={setRoomConflictErrors}
					/>
					{isOutletPanel && (
						<SourceSelector
						fromEdit={true}
						/>
					)}
					<PriceSummary />
					</div>
					<Notes
						fromEdit={true}
					/>

				</div>


				{showUpdateRepeatBooking && (
					<UpdateRepeatBooking 
						onClose={handleRepeatBookingSave}
						onCancel={handleRepeatBookingCancel}
						editableBooking={editableBooking}
					/>
				)}

				{/* PAYMENT IN PROGRESS START */}
				{isBookingPaymentInProgress && showPaymentInProgressNotice && (
				<div className={`
				${styles.overlappingErrorContainer} 
				${styles.paymentInProgress}
				${styles.checkoutInfoContainer}
				`}
				style={{ bottom: `calc(var(--sidebarBtnMainHeight) + ${overlappingErrorContainerHeight}px)` }}
				>
				{PAYMENT_IN_PROGRESS_MESSAGE}
				</div>
				)}
				{/* PAYMENT IN PROGRESS END */}


				{isBookingCompleted && completeBookingWarning && (
				<div className={`
				${styles.overlappingErrorContainer} 
				${styles.paymentInProgress}
				${styles.checkoutInfoContainer}
				`}
				style={{ bottom: `calc(var(--sidebarBtnMainHeight) + ${overlappingErrorContainerHeight}px)` }}
				>
				{COMPLETE_BOOKING_WARNING_MESSAGE}
				</div>
				)}

				{/* ERRORS CONTAINER START  */}
				{!isSubmitting && (overlappingErrors.length > 0 || roomConflictErrors.length > 0 || apiErrorForCreateBooking || saveValidationError || isGreyBox) && (
				<div
					ref={overlappingErrorContainerRef}
					className={`${styles.overlappingErrorContainer} ${canSave && styles.bottom}`}
				>
					{!isSubmitting && overlappingErrors.length > 0 && (
						<div>
							{/* Show the multi-therapist overlap message only once */}
							{overlappingErrors.some((e) => e.isCustomerTimeOverlap) && (
								<div className={styles.errorMessage}>Appointments overlap with multiple therapists</div>
							)}
							{/* Render other errors normally */}
							{overlappingErrors
								.filter((e) => !e.isCustomerTimeOverlap)
								.map((error, index) => (
									<div key={`err-${index}`} className={styles.errorMessage}>
										{error.isOverlappingWithBreaks
											? `Booking overlaps with ${error.therapistName}'s break`
											: error.isOutsideSchedule
											? `Booking outside of ${error.therapistName}'s working hours`
											: `One of more services have the same time`}
									</div>
								))}
						</div>
					)}

					{!isSubmitting && roomConflictErrors.length > 0 && (
						<div>
							{Array.from(new Set(roomConflictErrors)).map((message, index) => (
								<div key={`room-conflict-${index}`} className={styles.errorMessage}>
									{message}
								</div>
							))}
						</div>
					)}

					{saveValidationError && (
						<div className={styles.errorMessage}>{saveValidationError}</div>
					)}

					{apiErrorForCreateBooking && (
						<div className={styles.saleInProgressErrorRow}>
							<div className={styles.errorMessage}>{apiErrorForCreateBooking}</div>
							<button
								type="button"
								className={styles.reloadErrorButton}
								onClick={onClose}
							>
								<ReloadIcon size={12} color="currentColor" />
								Reload
							</button>
						</div>
					)}
					
					{isGreyBox && (
						<div className={styles.errorMessage}>
							{isRoomFilterActive 
								? "Outlet is closed, want to proceed?" 
								: `${unavailableTherapistName} is not available!`}
						</div>
					)}
				</div>
				)}
				{/* ERRORS CONTAINER END  */}



				<div className={styles.bookBtnContainer}>
					{canSave && (
						<PrimaryButton
							className={`${styles.fullWidthBtn} rounded-0`}
							type="button"
							disabled={isSubmitting || showUpdateRepeatBooking}
							text={isSubmitting ? "Processing..." : "Save Changes"}
							onClick={handleUpdate}
						/>
					)}
				</div>

			</div>
		</>
	);
}
);

export default EditSidebarWrapper;